import {environmentCommon} from '../../../environments/environment-common';
import {Platform, ProductLine} from '../../model/product/product.model';
import {URLS} from '../navigation/navigation.data';
import {PRODUCT_RIBBONS} from '../ribbon/ribbon.data';
import {TECH_STACK} from '../tech-stack/tech-stack.data';

const OPEN_SOURCE_FEATURE = {
  name: 'Open Source',
  description: 'No need to pay a price to get started. Get the latest updates from community driven open source project.',
};

const PROFESSIONAL_HELP_FEATURE = {
  name: 'Professional Help',
  description: 'Get an up and running server.',
};

const CUSTOMISED_SOLUTIONS_FEATURE = {
  name: 'Customised Solutions',
  description: 'Fully customized functional solutions for your needs.',
};

const CI_CD_AUTOMATION_FEATURE = {
  name: 'CI/CD Automation',
  description: 'Get a CI/CD integration with automated API tests.',
};

const CUSTOMISED_AUTOMATIONS_FEATURE = {
  name: 'Customised Automations',
  description: 'Fully customized automation solutions for your needs.',
};

const SECURITY_REPORTS_FEATURE = {
  name: 'Reports',
  description: 'Get a security report at the end of each month.',
};

const CUSTOM_SECURITY_COMPLIANCE_FEATURE = {
  name: 'Custom Security Compliance',
  description: 'Fully customized security compliance solutions for your needs.',
};

const DEFAULT_OPEN_SOURCE_SERVER_FEATURES = [
  {
    name: 'Core Features',
    details: [
      OPEN_SOURCE_FEATURE,
      PROFESSIONAL_HELP_FEATURE,
      CUSTOMISED_SOLUTIONS_FEATURE,
    ],
  },
  {
    name: 'Quality and DX',
    details: [
      CI_CD_AUTOMATION_FEATURE,
      CUSTOMISED_AUTOMATIONS_FEATURE,
    ],
  },
  {
    name: 'Security',
    details: [
      SECURITY_REPORTS_FEATURE,
      CUSTOM_SECURITY_COMPLIANCE_FEATURE,
    ],
  },
];

const FREE_PRICING_OPTION = {
  title: 'Free',
  description: 'For Individuals',
  price: {
    currency: '$',
    value: '0',
    subscriptionTime: 'mo.',
  },
  features: [['true', '', ''], ['', ''], ['', '']]
};

const STANDARD_PRICING_OPTION = {
  title: 'Standard',
  description: 'For Startups',
  ribbon: PRODUCT_RIBBONS.get('deal'),
  contactRequired: true,
  features: [['true', 'true', ''], ['true', ''], ['Up to 10 users', '']]
};

const ENTERPRISE_PRICING_OPTION = {
  title: 'Enterprise',
  description: 'For Enterprises',
  contactRequired: true,
  features: [['true', 'true', 'true'], ['true', 'true'], ['Unlimited users', 'true']]
};

const DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS = [
  FREE_PRICING_OPTION,
  STANDARD_PRICING_OPTION,
  ENTERPRISE_PRICING_OPTION,
];

export const PRODUCT_LINES: ProductLine[] = [
  {
    key: 'saas-solutions',
    name: 'Saas Solutions',
    description: 'Saas solutions for your needs',
    products: [
      {
        key: 'open-companies',
        name: 'Open Companies',
        description: 'Coming soon..',
        url: URLS.maintenance,
        logo: './assets/logo/brand-logo-filterable.webp'
      },
    ]
  },
];

export const PLUGINS: ProductLine[] = [
  {
    key: 'plugins',
    name: 'Chrome Plugins',
    description: 'Our browser extensions',
    products: [
      {
        key: 'chrome-color-picker',
        name: 'Color Picker',
        shortDescription: 'Eyedrop any color on screen and read it in every color space',
        description: `Pick any color on your screen with the eyedropper and read it in every color space, with contrast checks, harmonies and more, right from your toolbar. A free Chrome extension by Codigrate.`,
        url: URLS.plugins.chromeColorPicker,
        logo: './assets/product-lines/tools/chrome-color-picker.svg'
      },
    ]
  },
  {
    key: 'jetbrains-plugins',
    name: 'JetBrains Plugins',
    description: 'Our JetBrains IDE plugins',
    products: [
      {
        key: 'jetbrains-color-picker',
        name: 'Color Picker',
        shortDescription: 'Eyedrop any color and read everything about it in your IDE',
        description: `Pick any color on your screen with the eyedropper, then read everything about it in one tool window: conversions across every common color space, WCAG accessibility checks, harmonies, tints and more. A free JetBrains plugin by Codigrate.`,
        url: URLS.plugins.jetbrainsColorPicker,
        logo: './assets/product-lines/tools/color-picker.svg'
      },
    ]
  },
  {
    key: 'vscode-extensions',
    name: 'VS Code Extensions',
    description: 'Our VS Code extensions',
    products: [
      {
        key: 'vscode-color-picker',
        name: 'Color Picker',
        shortDescription: 'Eyedrop any color and read everything about it in your editor',
        description: `Pick any color on your screen with the eyedropper, or put the cursor on a color in your code, then read everything about it in one side bar panel: conversions across every common color space, WCAG accessibility checks, harmonies, tints and more. A free VS Code extension by Codigrate.`,
        url: URLS.plugins.vscodeColorPicker,
        logo: './assets/product-lines/tools/color-picker.svg'
      },
    ]
  },
  {
    key: 'macos-apps',
    name: 'macOS Apps',
    description: 'Our native Mac applications',
    products: [
      {
        key: 'macos-dock-town',
        name: 'Dock Town',
        shortDescription: 'Your Mac as a cozy town in the menu bar, growing while you work',
        description: `See how your Mac is doing in a cozy isometric town in the menu bar: traffic is the CPU, lit windows are memory, the sky is the network, a corner sign is the battery and smoke is heat. The time you work earns coins that build it, in 20+ towns from Tokyo to the Arctic. A native menu bar app by Codigrate.`,
        url: URLS.plugins.macosDockTown,
        // a picture of a town until the app has its icon (macos-dock-town-page.component.ts)
        logo: './assets/product-lines/tools/dock-town/icon.png'
      },
      {
        key: 'macos-tidy-desktop',
        name: 'Tidy Desktop: AI Organizer',
        shortDescription: 'Your desktop sorted into tidy collections by on-device AI',
        description: `Sort your Mac desktop into tidy collections by what each file is for, with Apple's on-device AI. Your files never move, new ones find their collection by themselves, and the 24 Codigrate themes color it all. A native menu bar app by Codigrate.`,
        url: URLS.plugins.macosTidyDesktop,
        logo: './assets/product-lines/tools/macos-tidy-desktop.png'
      },
      {
        key: 'macos-ai-token-meter',
        name: 'AI Token Meter',
        shortDescription: 'Your coding agents’ tokens and their cost at API prices, in the menu bar',
        description: `See the tokens Claude Code, Codex and Antigravity CLI use and what they would cost at API prices, right in your Mac menu bar: today's total, Claude's and Codex's limits, the sessions working now, and your history by model, project and session. It reads only token counts from the agents' own logs. A native menu bar app by Codigrate.`,
        url: URLS.plugins.macosAiTokenMeter,
        logo: './assets/product-lines/tools/ai-token-meter/icon.png'
      },
      {
        key: 'macos-color-picker',
        name: 'Color Picker',
        shortDescription: 'Eyedrop any pixel and read everything about it, from your menu bar',
        description: `Pick any color on your screen with the native macOS loupe, then read everything about it in one panel: every notation with copy-ready CSS, WCAG and APCA contrast, harmonies, tints and shades, temperature and color vision simulations. A native menu bar app by Codigrate.`,
        url: URLS.plugins.macosColorPicker,
        logo: './assets/product-lines/tools/macos-color-picker.png'
      },
    ]
  },
  {
    // After macOS Apps (owner, 2026-10-09), on the /plugins hub, the home page and in the footer.
    key: 'ios-apps',
    name: 'iOS Apps',
    description: 'Our iPhone and iPad applications',
    products: [
      {
        key: 'langmi',
        name: 'LangMi',
        shortDescription: 'Learn a language by living a story, with real voices',
        description: `Learn a language by living a story: choose your lines in real dialogues, say them out loud, and keep every word with a journal that brings it back before you forget. Mini games, badges and learning live with your teacher. An iPhone and iPad app by Codigrate.`,
        url: URLS.langmi,
        logo: './assets/product-lines/tools/langmi/icon.png'
      },
    ]
  },
];

export const LIBRARIES: ProductLine[] = [];

export const THEMES: ProductLine[] = [
  {
    key: 'nature',
    name: 'Nature',
    description: `Themes inspired by nature`,
    products: [
      {
        key: 'aurora-borealis',
        releaseDate: '2023-08-13',
        name: 'Aurora Borealis',
        editorialLabel: 'Must Have',
        description: `Inspired by the Arctic night sky and the slow drift of the Northern Lights, this theme blends deep navy and teal shadows with luminous blue and green accents to create a calm yet vivid coding environment. Dark, cool backgrounds keep the editor restful, while bright cyans, fresh greens, and soft lavenders add clarity and energy to key syntax elements.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'jetbrains-themes/tree/main/nature/aurora-borealis-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/22487-aurora-borealis-theme',
        logo: './assets/themes/nature/aurora-borealis-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Arctic', 'Cinematic', 'Luminous'],
        mainColors: ['Blue'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/22487-aurora-borealis-theme',
            externalId: '22487',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'aurora-borealis/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-aurora-borealis-theme',
            externalId: 'codigrate.cod-aurora-borealis-theme',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/aurora-borealis-theme/ggdeckhhnhopdjnnbngcflijkhnlodfk',
            externalId: 'ggdeckhhnhopdjnnbngcflijkhnlodfk',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-aurora-borealis/',
            externalId: 'codigrate-aurora-borealis',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-aurora-borealis-theme',
            externalId: 'codigrate/cod-aurora-borealis-theme',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'aurora-borealis/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/nature/aurora-borealis-theme/aurora-borealis-theme.txt',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'aurora-borealis/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/nature/aurora-borealis-theme/Codigrate%20Aurora%20Borealis.terminal',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'aurora-borealis/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/nature/aurora-borealis-theme/Codigrate%20Aurora%20Borealis.itermcolors',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'aurora-borealis/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/nature/aurora-borealis-theme/Codigrate%20Aurora%20Borealis',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'aurora-borealis/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/nature/aurora-borealis-theme/codigrate-aurora-borealis.json',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'aurora-borealis/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/nature/aurora-borealis-theme/codigrate-aurora-borealis.tmTheme',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'aurora-borealis/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/nature/aurora-borealis-theme/codigrate-aurora-borealis.json',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'aurora-borealis/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/nature/aurora-borealis-theme/Codigrate%20Aurora%20Borealis.zip',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'aurora-borealis/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/nature/aurora-borealis-theme/themes/aurora-borealis.json',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'aurora-borealis/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/nature/aurora-borealis-theme/Codigrate%20Aurora%20Borealis.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            status: 'done',
            url: 'https://line.me/S/shop/theme/detail?id=cbdc209f-6fb3-4abd-82c6-ea2ca267d3f2',
            screenshot: {
              src: './assets/themes/nature/aurora-borealis-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src: './assets/themes/nature/aurora-borealis-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#123243',
          '#549EFF',
          '#1ecfb6',
          '#BB719B',
          '#BAA5FF',
        ],
        palette: [
          '#142B37',
          '#123243',
          '#549EFF',
          '#7ACEF5',
          '#043A33',
          '#73D379',
          '#05C0A6',
          '#BB719B',
          '#D193BB',
          '#BAA5FF',
        ],
        designTokens: {
          "primaryColor": '#6799dc',
          "secondaryColor": '#4dc1b1',
          "tertiaryColor": '#BB719B',
          "editorBackground": "#142B37",
          "hover": "#643c59",
          "inputBackground": "#112733",
          "editorForeground": "#B0B9BE",
          "error": "#D0636C",
          "errorFocused": "#D74E5B",
          "info": "#5AA6DA",
          "infoFocused": "#3EA0E3",
          "primaryForeground": "#B0B9BE",
          "secondaryForeground": "#85A2B3",
          "success": "#5BC093",
          "successFocused": "#3FC88B",
          "surface": "#1A3746",
          "warning": "#CBA45E",
          "warningFocused": "#D49A3C",
          "windowBackground": "#123243",
        }
      },
      {
        key: 'everest',
        releaseDate: '2023-09-02',
        name: 'Everest',
        editorialLabel: 'Must Have',
        description: `Inspired by the icy terraces of Mount Everest and the first light breaking over its peaks, this theme pairs cool white and pale grey backgrounds with deep glacier blues and greens to create a clear and unhurried coding environment. Bright, airy surfaces keep the editor calm, while teals, forest greens, and a warm coral accent add definition and focus to key syntax elements.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'jetbrains-themes/tree/main/nature/everest-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/22653-everest-theme',
        logo: './assets/themes/nature/everest-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Calm', 'Icy', 'Sunrise'],
        mainColors: ['Blue'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/22653-everest-theme',
            externalId: '22653',
            screenshot: {
              src: './assets/themes/nature/everest-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'everest/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/everest-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-everest-theme',
            externalId: 'codigrate.cod-everest-theme',
            screenshot: {
              src: './assets/themes/nature/everest-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/everest-theme/kcldghlfficdfjjjcenephkkdkcjngic',
            externalId: 'kcldghlfficdfjjjcenephkkdkcjngic',
            screenshot: {
              src: './assets/themes/nature/everest-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-everest/',
            externalId: 'codigrate-everest',
            screenshot: {
              src: './assets/themes/nature/everest-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-everest-theme',
            externalId: 'codigrate/cod-everest-theme',
            screenshot: {
              src: './assets/themes/nature/everest-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'everest/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/nature/everest-theme/everest-theme.txt',
            screenshot: {
              src: './assets/themes/nature/everest-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'everest/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/nature/everest-theme/Codigrate%20Everest.terminal',
            screenshot: {
              src: './assets/themes/nature/everest-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'everest/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/nature/everest-theme/Codigrate%20Everest.itermcolors',
            screenshot: {
              src: './assets/themes/nature/everest-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'everest/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/nature/everest-theme/Codigrate%20Everest',
            screenshot: {
              src: './assets/themes/nature/everest-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'everest/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/nature/everest-theme/codigrate-everest.json',
            screenshot: {
              src: './assets/themes/nature/everest-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'everest/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/nature/everest-theme/codigrate-everest.tmTheme',
            screenshot: {
              src: './assets/themes/nature/everest-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'everest/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/nature/everest-theme/codigrate-everest.json',
            screenshot: {
              src: './assets/themes/nature/everest-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'everest/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/nature/everest-theme/Codigrate%20Everest.zip',
            screenshot: {
              src: './assets/themes/nature/everest-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'everest/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/nature/everest-theme/themes/everest.json',
            screenshot: {
              src: './assets/themes/nature/everest-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'everest/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/nature/everest-theme/Codigrate%20Everest.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/nature/everest-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            status: 'done',
            url: 'https://line.me/S/shop/theme/detail?id=972b4ad2-b4ac-472c-9329-bf9523353dc6',
            screenshot: {
              src: './assets/themes/nature/everest-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src: './assets/themes/nature/everest-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#E4ECEF',
          '#1A6D9F',
          '#005E79',
          '#ED7E5A',
          '#8F4446'
        ],
        palette: [
          '#FDFEFF',
          '#E4ECEF',
          '#1A6D9F',
          '#005E79',
          '#007A47',
          '#2E674F',
          '#83529B',
          '#8C4069',
          '#ED7E5A',
          '#8F4446',
        ],
        designTokens: {
          "primaryColor": '#7db1cf',
          "secondaryColor": '#e1a794',
          "tertiaryColor": '#83529B',
          "editorBackground": "#FDFEFF",
          "hover": "#d5ece2",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#0E3448",
          "error": "#C8534E",
          "errorFocused": "#D13731",
          "info": "#397FB7",
          "infoFocused": "#2876B4",
          "primaryForeground": "#0E3448",
          "secondaryForeground": "#567B8A",
          "success": "#30A25E",
          "successFocused": "#219D53",
          "surface": "#CADAE0",
          "warning": "#C8963E",
          "warningFocused": "#C98F28",
          "windowBackground": "#E4ECEF",
        }
      },
      {
        key: 'sequoia',
        releaseDate: '2023-09-10',
        name: 'Sequoia',
        editorialLabel: 'Hidden Gem',
        description: `Inspired by the towering stillness of a sequoia grove, this theme blends deep forest black and bark brown with living green and moss accents to create a grounded and focused coding environment. Dark, earthy backgrounds settle the editor, while warm tans, rusts, and a clear canopy green add contrast and clarity to key syntax elements.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'jetbrains-themes/tree/main/nature/sequoia-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/22826-sequoia-theme',
        logo: './assets/themes/nature/sequoia-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Forest', 'Earthy', 'Calm'],
        mainColors: ['Green'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/22826-sequoia-theme',
            externalId: '22826',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'sequoia/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-sequoia-theme',
            externalId: 'codigrate.cod-sequoia-theme',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/sequoia-theme/elbdggfmdikianlcniekopdflpkppnoj',
            externalId: 'elbdggfmdikianlcniekopdflpkppnoj',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-sequoia/',
            externalId: 'codigrate-sequoia',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-sequoia-theme',
            externalId: 'codigrate/cod-sequoia-theme',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'sequoia/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/nature/sequoia-theme/sequoia-theme.txt',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'sequoia/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/nature/sequoia-theme/Codigrate%20Sequoia.terminal',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'sequoia/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/nature/sequoia-theme/Codigrate%20Sequoia.itermcolors',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'sequoia/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/nature/sequoia-theme/Codigrate%20Sequoia',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'sequoia/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/nature/sequoia-theme/codigrate-sequoia.json',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'sequoia/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/nature/sequoia-theme/codigrate-sequoia.tmTheme',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'sequoia/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/nature/sequoia-theme/codigrate-sequoia.json',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'sequoia/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/nature/sequoia-theme/Codigrate%20Sequoia.zip',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'sequoia/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/nature/sequoia-theme/themes/sequoia.json',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'sequoia/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/nature/sequoia-theme/Codigrate%20Sequoia.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            status: 'done',
            url: 'https://line.me/S/shop/theme/detail?id=9a23ac7f-5583-4aa7-a0b4-2dc186b67232',
            screenshot: {
              src: './assets/themes/nature/sequoia-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src: './assets/themes/nature/sequoia-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#405133',
          '#6E9F56',
          '#A68F59',
          '#986969',
          '#6C625A'
        ],
        palette: [
          '#20231C',
          '#32382C',
          '#405133',
          '#6E9F56',
          '#369772',
          '#A68F59',
          '#A67B5B',
          '#A86255',
          '#986969',
          '#6C625A',
        ],
        designTokens: {
          "primaryColor": '#5e8a3e',
          "secondaryColor": '#b6a36f',
          "tertiaryColor": '#A86255',
          "editorBackground": "#20231C",
          "hover": "#453a34",
          "inputBackground": "#0E0E0E",
          "editorForeground": "#BABEBA",
          "error": "#C2564B",
          "errorFocused": "#C73F31",
          "info": "#4A93C0",
          "infoFocused": "#328CC4",
          "primaryForeground": "#BABEBA",
          "secondaryForeground": "#849184",
          "success": "#5CB36A",
          "successFocused": "#42B955",
          "surface": "#394132",
          "warning": "#C39A45",
          "warningFocused": "#C6942E",
          "windowBackground": "#32382C",
        }
      },
      {
        key: 'autumn',
        releaseDate: '2023-10-01',
        name: 'Autumn',
        editorialLabel: 'Popular',
        description: `Inspired by fallen leaves and late afternoon sunlight, this theme blends soft parchment backgrounds with rust, copper, and harvest gold to create a warm and unhurried coding environment. Light, gentle surfaces keep the editor comfortable, while deep greens and a quiet teal add contrast and focus to key syntax elements.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'jetbrains-themes/tree/main/nature/autumn-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/22700-autumn-theme',
        logo: './assets/themes/nature/autumn-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Cozy', 'Warm', 'Earthy'],
        mainColors: ['Orange'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/22700-autumn-theme',
            externalId: '22700',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'autumn/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-autumn-theme',
            externalId: 'codigrate.cod-autumn-theme',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/autumn-theme/fffebmlejekcghghcmiinjamjdcbbojc',
            externalId: 'fffebmlejekcghghcmiinjamjdcbbojc',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-autumn/',
            externalId: 'codigrate-autumn',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-autumn-theme',
            externalId: 'codigrate/cod-autumn-theme',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'autumn/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/nature/autumn-theme/autumn-theme.txt',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'autumn/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/nature/autumn-theme/Codigrate%20Autumn.terminal',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'autumn/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/nature/autumn-theme/Codigrate%20Autumn.itermcolors',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'autumn/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/nature/autumn-theme/Codigrate%20Autumn',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'autumn/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/nature/autumn-theme/codigrate-autumn.json',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'autumn/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/nature/autumn-theme/codigrate-autumn.tmTheme',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'autumn/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/nature/autumn-theme/codigrate-autumn.json',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'autumn/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/nature/autumn-theme/Codigrate%20Autumn.zip',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'autumn/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/nature/autumn-theme/themes/autumn.json',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'autumn/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/nature/autumn-theme/Codigrate%20Autumn.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store again since 2026-08-19 — it was pulled back
            // for edits after its first run, and the re-submission cleared review
            // under a NEW detail id, so the old ef7eeec5 link is dead
            url: 'https://line.me/S/shop/theme/detail?id=d85c981b-41a3-4fe7-81ca-37e2aa0b4102',
            screenshot: {
              src: './assets/themes/nature/autumn-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src: './assets/themes/nature/autumn-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#F4D3BD',
          '#BE553E',
          '#B0633A',
          '#DEA51D',
          '#0E8113'
        ],
        palette: [
          '#FCFBFA',
          '#F8F4F1',
          '#F4D3BD',
          '#BE553E',
          '#773918',
          '#B0633A',
          '#A87F25',
          '#1B591E',
          '#0E8113',
          '#006E83',
        ],
        designTokens: {
          "primaryColor": '#ec925b',
          "secondaryColor": '#e4c069',
          "tertiaryColor": '#E07431',
          "editorBackground": "#FCFBFA",
          "hover": "#dfefcb",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#2E1B0F",
          "error": "#C8504A",
          "errorFocused": "#D0352D",
          "info": "#397FB7",
          "infoFocused": "#2876B4",
          "primaryForeground": "#2E1B0F",
          "secondaryForeground": "#82614C",
          "success": "#30A25E",
          "successFocused": "#219D53",
          "surface": "#EFE6E0",
          "warning": "#D49A2A",
          "warningFocused": "#D0921A",
          "windowBackground": "#F8F4F1",
        }
      },
      {
        key: 'roraima',
        releaseDate: '2023-10-08',
        editorialLabel: 'Hidden Gem',
        name: 'Roraima',
        description: `Inspired by the sunset over Mount Roraima and the slow turn from day to night on its tabletop summit, this theme blends deep brown and clay shadows with amber, gold, and salmon accents to create a soothing yet vivid coding environment. Dark, earthy backgrounds ground the editor, while soft violets and mineral yellows add contrast and focus to key syntax elements.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'jetbrains-themes/tree/main/nature/roraima-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/22961-roraima-theme',
        logo: './assets/themes/nature/roraima-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Sunset', 'Mystical', 'Atmospheric'],
        mainColors: ['Brown', 'Orange'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/22961-roraima-theme',
            externalId: '22961',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'roraima/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-roraima-theme',
            externalId: 'codigrate.cod-roraima-theme',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/roraima-theme/djgpkjnnddponeeeefijcpballhflkgd',
            externalId: 'djgpkjnnddponeeeefijcpballhflkgd',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-roraima/',
            externalId: 'codigrate-roraima',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-roraima-theme',
            externalId: 'codigrate/cod-roraima-theme',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'roraima/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/nature/roraima-theme/roraima-theme.txt',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'roraima/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/nature/roraima-theme/Codigrate%20Roraima.terminal',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'roraima/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/nature/roraima-theme/Codigrate%20Roraima.itermcolors',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'roraima/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/nature/roraima-theme/Codigrate%20Roraima',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'roraima/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/nature/roraima-theme/codigrate-roraima.json',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'roraima/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/nature/roraima-theme/codigrate-roraima.tmTheme',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'roraima/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/nature/roraima-theme/codigrate-roraima.json',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'roraima/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/nature/roraima-theme/Codigrate%20Roraima.zip',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'roraima/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/nature/roraima-theme/themes/roraima.json',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'roraima/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/nature/roraima-theme/Codigrate%20Roraima.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            url: 'https://line.me/S/shop/theme/detail?id=61244356-778c-4b75-a916-88e5db21ad91',
            screenshot: {
              src: './assets/themes/nature/roraima-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src: './assets/themes/nature/roraima-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#322628',
          '#D17458',
          '#DDBE6D',
          '#ED8B8B',
          '#8F78B7'
        ],
        palette: [
          '#1E1A1B',
          '#322628',
          '#582F29',
          '#D17458',
          '#D69568',
          '#D1BA46',
          '#DDBE6D',
          '#ED8B8B',
          '#8F78B7',
          '#7E6AA3',
        ],
        designTokens: {
          "primaryColor": '#9c5a4e',
          "secondaryColor": '#8F78B7',
          "tertiaryColor": '#D17458',
          "editorBackground": "#1E1A1B",
          "hover": "#562f2f",
          "inputBackground": "#171415",
          "editorForeground": "#CEC1C1",
          "error": "#CE4A44",
          "errorFocused": "#D72E27",
          "info": "#5292C7",
          "infoFocused": "#358AD0",
          "primaryForeground": "#CEC1C1",
          "secondaryForeground": "#AD8A8A",
          "success": "#4CC27B",
          "successFocused": "#32C86E",
          "surface": "#372C2F",
          "warning": "#CDA644",
          "warningFocused": "#D5A427",
          "windowBackground": "#322628",
        }
      },
      {
        key: 'sakura',
        releaseDate: '2023-10-17',
        name: 'Sakura',
        editorialLabel: 'Popular',
        description: `Inspired by cherry blossom season and the quiet gardens beneath it, this theme pairs pale petal backgrounds with deep rose and plum accents to create a soft and unhurried coding environment. Light, gentle surfaces keep the editor calm, while muted greens and clear sky blues add balance and focus to key syntax elements.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'jetbrains-themes/tree/main/nature/sakura-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/22872-sakura-theme',
        logo: './assets/themes/nature/sakura-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Pastel', 'Soft', 'Elegant'],
        mainColors: ['Pink'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/22872-sakura-theme',
            externalId: '22872',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'sakura/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-sakura-theme',
            externalId: 'codigrate.cod-sakura-theme',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/sakura-theme/nnildobojpcnfhiihnjklleoimimmkcc',
            externalId: 'nnildobojpcnfhiihnjklleoimimmkcc',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-sakura/',
            externalId: 'codigrate-sakura',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-sakura-theme',
            externalId: 'codigrate/cod-sakura-theme',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'sakura/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/nature/sakura-theme/sakura-theme.txt',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'sakura/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/nature/sakura-theme/Codigrate%20Sakura.terminal',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'sakura/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/nature/sakura-theme/Codigrate%20Sakura.itermcolors',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'sakura/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/nature/sakura-theme/Codigrate%20Sakura',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'sakura/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/nature/sakura-theme/codigrate-sakura.json',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'sakura/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/nature/sakura-theme/codigrate-sakura.tmTheme',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'sakura/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/nature/sakura-theme/codigrate-sakura.json',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'sakura/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/nature/sakura-theme/Codigrate%20Sakura.zip',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'sakura/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/nature/sakura-theme/themes/sakura.json',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'sakura/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/nature/sakura-theme/Codigrate%20Sakura.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            status: 'done',
            url: 'https://line.me/S/shop/theme/detail?id=e46116db-7cae-4450-a02d-cded0bea6dc8',
            screenshot: {
              src: './assets/themes/nature/sakura-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src: './assets/themes/nature/sakura-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#FFC9DC',
          '#CB6B91',
          '#DBEACB',
          '#69A2BD',
          '#607FA9'
        ],
        palette: [
          '#FEFCFC',
          '#F8DBE6',
          '#FFC9DC',
          '#CB6B91',
          '#98556C',
          '#DBEACB',
          '#618C71',
          '#69A2BD',
          '#607FA9',
          '#687788',
        ],
        designTokens: {
          "primaryColor": '#e898b4',
          "secondaryColor": '#b4d592',
          "tertiaryColor": '#8BCF95',
          "editorBackground": "#FEFCFC",
          "hover": "#e7cfdf",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#3D0013",
          "error": "#BE5C55",
          "errorFocused": "#C64238",
          "info": "#397FB7",
          "infoFocused": "#2876B4",
          "primaryForeground": "#3D0013",
          "secondaryForeground": "#94667D",
          "success": "#30A25E",
          "successFocused": "#219D53",
          "surface": "#F8DBE6",
          "warning": "#BC9C5C",
          "warningFocused": "#C49840",
          "windowBackground": "#FCF4F7",
        }
      },
      {
        key: 'ocean',
        releaseDate: '2025-12-27',
        name: 'Ocean',
        editorialLabel: 'Highly Recommended',
        description: `Inspired by the deep and tranquil world of the ocean, this dark theme seamlessly blends layered dark teal tones with soft cyan highlights and subtle bioluminescent accents. This balanced palette offers a calm and immersive backdrop, ensuring an optimal and focused coding experience.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/nature/ocean-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34257-ocean-theme',
        logo:
          './assets/themes/nature/ocean-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Deep', 'Tranquil', 'Immersive'],
        mainColors: ['Teal'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34257-ocean-theme',
            externalId: '34257',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'ocean/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'ocean/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'ocean/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download), not on a
            // marketplace: the sku names the store listing, the price comes from
            // the store catalogue, and there is no url to link out to. The zip
            // ships the color scheme + the window theme (see base-themes
            // generators/sublime.js --premium and STORE-DESIGN.md).
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'ocean/sublime-text',
            // display fallback; the Stripe Price is what is charged — keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'ocean/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'ocean/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'ocean/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'ocean/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'ocean/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'ocean/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'ocean/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'ocean/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=21a24cf8-c1f6-4cec-be3d-9171078a6432',
            screenshot: {
              src: './assets/themes/nature/ocean-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/nature/ocean-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#1C4247',
          '#5AB590',
          '#3FB4D8',
          '#DC7783',
          '#EAC089'
        ],
        palette: [
          '#102B2E',
          '#18383C',
          '#1C4247',
          '#5AB590',
          '#3FB4D8',
          '#B88BDA',
          '#EE8EBF',
          '#DC7783',
          '#DC9577',
          '#EAC089',
        ],
        designTokens: {
          "primaryColor": '#188e78',
          "secondaryColor": '#DC7783',
          "tertiaryColor": '#1C4247',
          "editorBackground": "#102B2E",
          "hover": "#3e3136",
          "inputBackground": "#0B2225",
          "editorForeground": "#AFBABD",
          "error": "#C74D5C",
          "errorFocused": "#D02F43",
          "info": "#4A9BD8",
          "infoFocused": "#2C94E2",
          "primaryForeground": "#AFBABD",
          "secondaryForeground": "#88AAB7",
          "success": "#52BA88",
          "successFocused": "#39BF7E",
          "surface": "#1C4247",
          "warning": "#D6A44E",
          "warningFocused": "#DF9F30",
          "windowBackground": "#18383C",
        }
      },
      {
        key: 'salda',
        releaseDate: '2025-12-30',
        name: 'Salda',
        editorialLabel: 'Designer\'s Choice',
        description: `Inspired by the crystalline waters and bright white shores of Salda Lake, this light theme brings a fresh, airy calm to your IDE with clean lake blues and vibrant turquoise tones. Soft beige keeps everything crisp and readable on a clear, spacious backdrop. The palette is designed to be gentle on the eyes, helping you stay focused and code with clarity.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/nature/salda-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34265-salda-theme',
        logo:
          './assets/themes/nature/salda-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Airy', 'Fresh', 'Coastal'],
        mainColors: ['Yellow', 'Beige'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34265-salda-theme',
            externalId: '34265',
            screenshot: {
              src: './assets/themes/nature/salda-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'salda/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'salda/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'salda/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'salda/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'salda/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'salda/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'salda/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'salda/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'salda/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'salda/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'salda/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'salda/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/salda-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=a2d2675d-736c-428d-bd6e-350b21fb4980',
            screenshot: {
              src: './assets/themes/nature/salda-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/nature/salda-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#FDF6E7',
          '#ECE1C9',
          '#A59379',
          '#B8E4EC',
          '#1397AE'
        ],
        palette: [
          '#FDFCFA',
          '#FDF6E7',
          '#ECE1C9',
          '#A59379',
          '#8F6055',
          '#B8E4EC',
          '#1397AE',
          '#44849F',
          '#277878',
          '#0F9363'
        ],
        designTokens: {
          "primaryColor": '#c8b595',
          "secondaryColor": '#7cd7e6',
          "tertiaryColor": '#FFFFFF',
          "editorBackground": "#FDFCFA",
          "hover": "#ead5bf",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#312408",
          "error": "#D05B52",
          "errorFocused": "#D94035",
          "info": "#3C8CCE",
          "infoFocused": "#2483D2",
          "primaryForeground": "#312408",
          "secondaryForeground": "#685C43",
          "success": "#3ACA68",
          "successFocused": "#26CA5A",
          "surface": "#ECE1C9",
          "warning": "#D5BA63",
          "warningFocused": "#DDB947",
          "windowBackground": "#FDF6E7",
        }
      },
      {
        key: 'reynisfjara',
        releaseDate: '2026-03-05',
        name: 'Reynisfjara',
        editorialLabel: 'Highly Recommended',
        description: `Inspired by the dramatic and windswept shores of Reynisfjara, this dark theme blends deep charcoal surfaces with cool steel-blue tones and subtle icy highlights. The layered contrast between shadowy backgrounds and soft gray-blue accents creates a calm yet striking visual balance, offering a focused and immersive coding environment.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/nature/reynisfjara-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34268-reynisfjara-theme',
        logo:
          './assets/themes/nature/reynisfjara-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Nordic', 'Muted', 'Cinematic'],
        mainColors: ['Gray', 'Black'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34268-reynisfjara-theme',
            externalId: '34268',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'reynisfjara/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'reynisfjara/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'reynisfjara/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'reynisfjara/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'reynisfjara/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'reynisfjara/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'reynisfjara/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'reynisfjara/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'reynisfjara/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'reynisfjara/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'reynisfjara/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'reynisfjara/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=de7d1618-4148-4830-abba-9eefb9e05ac2',
            screenshot: {
              src: './assets/themes/nature/reynisfjara-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/nature/reynisfjara-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#2A3138',
          '#7D848D',
          '#8E8276',
          '#AC8563',
          '#B1AC9B'
        ],
        palette: [
          '#060709',
          '#151A20',
          '#20262C',
          '#2A3138',
          '#7D848D',
          '#8E8276',
          '#AC8563',
          '#C5A98F',
          '#C3B9AD',
          '#B1AC9B',
        ],
        designTokens: {
          "primaryColor": '#697a93',
          "secondaryColor": '#abb3bf',
          "tertiaryColor": '#8E8276',
          "editorBackground": "#151A20",
          "hover": "#3e2e35",
          "inputBackground": "#060709",
          "editorForeground": "#AEB6C0",
          "error": "#BD6F66",
          "errorFocused": "#C2594D",
          "info": "#6C98B7",
          "infoFocused": "#5490BB",
          "primaryForeground": "#AEB6C0",
          "secondaryForeground": "#8B95A2",
          "success": "#6EAB82",
          "successFocused": "#56AF73",
          "surface": "#2A3138",
          "warning": "#BA9D5F",
          "warningFocused": "#BF9846",
          "windowBackground": "#20262C",
        }
      },
      {
        key: 'spring',
        releaseDate: '2026-04-03',
        name: 'Spring',
        editorialLabel: 'Hidden Gem',
        description: `Inspired by the vibrant and refreshing essence of the spring season, this light theme brings a sense of renewal to your coding environment with soft greens, floral pinks and warm earth tones. Together they make a bright yet soothing palette, designed to enhance focus and provide a pleasant visual experience during long development sessions.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/nature/spring-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34269-spring-theme',
        logo:
          './assets/themes/nature/spring-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Floral', 'Refreshing', 'Gentle'],
        mainColors: ['Green'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34269-spring-theme',
            externalId: '34269',
            screenshot: {
              src: './assets/themes/nature/spring-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'spring/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'spring/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'spring/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'spring/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'spring/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'spring/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'spring/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'spring/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'spring/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'spring/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'spring/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'spring/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/spring-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=0cc520e6-f169-43bf-a927-db12f392a2fb',
            screenshot: {
              src: './assets/themes/nature/spring-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/nature/spring-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#C8EAB9',
          '#559569',
          '#BA6173',
          '#D5B3F4',
          '#5E9AAE'
        ],
        palette: [
          '#F4FFF1',
          '#C8EAB9',
          '#559569',
          '#3C8F63',
          '#BA6173',
          '#DC8395',
          '#8C68AC',
          '#D5B3F4',
          '#527FB6',
          '#5E9AAE'
        ],
        designTokens: {
          "primaryColor": '#99c685',
          "secondaryColor": '#e8a3b1',
          "tertiaryColor": '#559569',
          "editorBackground": "#FAFFF9",
          "hover": "#f4e1e7",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#122C07",
          "error": "#CE5563",
          "errorFocused": "#D7384A",
          "info": "#397FB7",
          "infoFocused": "#2876B4",
          "primaryForeground": "#122C07",
          "secondaryForeground": "#49613F",
          "success": "#73C71A",
          "successFocused": "#69C00C",
          "surface": "#C8EAB9",
          "warning": "#D1D72C",
          "warningFocused": "#CFD619",
          "windowBackground": "#F4FFF1",
        }
      },
      {
        key: 'etna',
        releaseDate: '2026-06-28',
        name: 'Etna',
        editorialLabel: 'Trending',
        description: `Inspired by Mount Etna's nighttime eruptions, this dark theme builds on an ashen basalt grey base and layers in the incandescent reds, oranges, and golds of molten lava. The near-neutral charcoal surface keeps long sessions calm, while the warm lava accents draw the eye to what matters.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/nature/etna-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34272-etna-theme',
        logo:
          './assets/themes/nature/etna-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Ember', 'Earthy', 'Cinematic'],
        mainColors: ['Red', 'Black'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34272-etna-theme',
            externalId: '34272',
            screenshot: {
              src: './assets/themes/nature/etna-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'etna/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'etna/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'etna/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'etna/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'etna/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'etna/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'etna/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'etna/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'etna/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'etna/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'etna/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'etna/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/etna-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-19 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=5a6e3e54-85e9-4753-83c2-ee139f0261f0',
            screenshot: {
              src: './assets/themes/nature/etna-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/nature/etna-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#222121',
          '#D74C57',
          '#C66439',
          '#E2BF79',
          '#D5CFCF'
        ],
        palette: [
          '#121111',
          '#1E1D1D',
          '#222121',
          '#D74C57',
          '#F1735F',
          '#C66439',
          '#E67748',
          '#E2BF79',
          '#B3AEAE',
          '#D5CFCF',
        ],
        designTokens: {
          "primaryColor": '#d13b47',
          "secondaryColor": '#E67748',
          "tertiaryColor": '#B3AEAE',
          "editorBackground": "#121111",
          "hover": "#191818",
          "inputBackground": "#080808",
          "editorForeground": "#D5CFCF",
          "error": "#CF383C",
          "errorFocused": "#E23A42",
          "info": "#6A96C8",
          "infoFocused": "#4F8BCF",
          "primaryForeground": "#D5CFCF",
          "secondaryForeground": "#B3AEAE",
          "success": "#85B66F",
          "successFocused": "#75BC54",
          "surface": "#222121",
          "warning": "#CDA337",
          "warningFocused": "#CD9D23",
          "windowBackground": "#1E1D1D",
        }
      },
      {
        key: 'fuji',
        releaseDate: '2026-08-01',
        name: 'Fuji',
        editorialLabel: 'New Release',
        description: `Inspired by Mount Fuji at first light, this light theme rests on the soft coral-peach glow of dawn over snow white, then lifts it with sakura pink and a lakeside torii's vermilion. The calm dawn surface keeps long sessions easy on the eyes while the blossom accents mark what matters.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/nature/fuji-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34273-fuji-theme',
        logo:
          './assets/themes/nature/fuji-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Sunrise', 'Pastel', 'Warm'],
        mainColors: ['Orange', 'Pink'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34273-fuji-theme',
            externalId: '34273',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'fuji/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'fuji/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'fuji/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'fuji/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'fuji/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'fuji/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'fuji/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'fuji/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'fuji/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'fuji/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'fuji/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'fuji/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-19 — the last of the 22 packs
            // built, and it cleared review, so the placeholder store root is
            // replaced by its own detail id and the entry is `done`. There is no
            // `status` key on a live platform: `done` is the default, and only
            // `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=9ba2e182-d18c-45fd-9846-8f4a23f3afd7',
            screenshot: {
              src: './assets/themes/nature/fuji-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/nature/fuji-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#F5D9D0',
          '#DD747A',
          '#BF8AB3',
          '#5F7D9F',
          '#39A1A5',
        ],
        palette: [
          '#FFFCFB',
          '#F5D9D0',
          '#DC5B26',
          '#D37154',
          '#DD747A',
          '#ED84B0',
          '#BF8AB3',
          '#39A1A5',
          '#5F7D9F',
          '#45302B',
        ],
        designTokens: {
          "primaryColor": '#DD747A',
          "secondaryColor": '#e6adc5',
          "tertiaryColor": '#39A1A5',
          "editorBackground": "#FFFCFB",
          "hover": "#F8E1D9",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#45302B",
          "error": "#C8453A",
          "errorFocused": "#D6382B",
          "info": "#2E7DC4",
          "infoFocused": "#1F6FB8",
          "primaryForeground": "#45302B",
          "secondaryForeground": "#8A6A61",
          "success": "#3E8E5E",
          "successFocused": "#2E9052",
          "surface": "#F5D9D0",
          "warning": "#D69A2E",
          "warningFocused": "#D3901C",
          "windowBackground": "#FBEAE3",
        }
      },
      {
        key: 'bioluminescence',
        releaseDate: '2026-10-30',
        name: 'Bioluminescence',
        editorialLabel: 'New Release',
        description: `Inspired by a bioluminescent shore at night, where every breaking wave lights up in electric blue, this dark theme layers abyss black and midnight navy with glowing blues and violet accents. The deep ocean surface keeps long sessions calm, while the luminous blues draw the eye to what matters.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/nature/bioluminescence-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/35642-bioluminescence-theme',
        logo:
          './assets/themes/nature/bioluminescence-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Neon', 'Luminous', 'Tranquil'],
        mainColors: ['Blue', 'Black'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/35642-bioluminescence-theme',
            externalId: '35642',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'bioluminescence/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'bioluminescence/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'bioluminescence/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'bioluminescence/sublime-text',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'bioluminescence/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'bioluminescence/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'bioluminescence/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'bioluminescence/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'bioluminescence/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'bioluminescence/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'bioluminescence/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'bioluminescence/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/nature/bioluminescence-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/nature/bioluminescence-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#0E1526',
          '#2F7BFF',
          '#8F7CFF',
          '#36D4F0',
          '#D7E3F2'
        ],
        palette: [
          '#06080F',
          '#0D1220',
          '#0E1526',
          '#2F7BFF',
          '#A6B4FF',
          '#8F7CFF',
          '#36D4F0',
          '#ACDAF6',
          '#9CB0C8',
          '#D7E3F2',
        ],
        designTokens: {
          "primaryColor": '#2F7BFF',
          "secondaryColor": '#8F7CFF',
          "tertiaryColor": '#9CB0C8',
          "editorBackground": "#06080F",
          "hover": "#0A0E1A",
          "inputBackground": "#03060D",
          "editorForeground": "#D7E3F2",
          "error": "#E8545C",
          "errorFocused": "#F0474F",
          "info": "#9A8CF0",
          "infoFocused": "#8676F0",
          "primaryForeground": "#D7E3F2",
          "secondaryForeground": "#9CB0C8",
          "success": "#85B66F",
          "successFocused": "#75BC54",
          "surface": "#0E1526",
          "warning": "#F07AB8",
          "warningFocused": "#F55FA8",
          "windowBackground": "#0D1220",
        }
      }
    ],
  },
  {
    key: 'cities',
    name: 'Cities',
    description: `Themes inspired by cities`,
    products: [
      {
        key: 'miami',
        releaseDate: '2026-01-01',
        name: 'Miami',
        editorialLabel: 'Vibe Coder\'s Choice',
        description: `Inspired by the electric nights and pastel sunsets of Miami, this theme blends deep purples with vibrant neon accents to create a bold yet balanced coding environment. Dark, warm backgrounds ground the editor, while vivid pinks, corals, and tropical teals bring energy and clarity to key syntax elements.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/miami-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/29582-miami-theme',
        logo:
          './assets/themes/cities/miami-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Neon', 'Tropical', 'Sunset'],
        mainColors: ['Pink'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/29582-miami-theme',
            externalId: '29582',
            screenshot: {
              src: './assets/themes/cities/miami-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'miami/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/miami-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-miami-theme',
            externalId: 'codigrate.cod-miami-theme',
            screenshot: {
              src: './assets/themes/cities/miami-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/miami-theme/kcmjhfhghepidmmaklccljefhjgfnlbc',
            externalId: 'kcmjhfhghepidmmaklccljefhjgfnlbc',
            screenshot: {
              src: './assets/themes/cities/miami-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-miami/',
            externalId: 'codigrate-miami',
            screenshot: {
              src: './assets/themes/cities/miami-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-miami-theme',
            externalId: 'codigrate/cod-miami-theme',
            screenshot: {
              src: './assets/themes/cities/miami-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'miami/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/cities/miami-theme/miami-theme.txt',
            screenshot: {
              src: './assets/themes/cities/miami-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'miami/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/cities/miami-theme/Codigrate%20Miami.terminal',
            screenshot: {
              src: './assets/themes/cities/miami-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'miami/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/cities/miami-theme/Codigrate%20Miami.itermcolors',
            screenshot: {
              src: './assets/themes/cities/miami-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'miami/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/cities/miami-theme/Codigrate%20Miami',
            screenshot: {
              src: './assets/themes/cities/miami-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'miami/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/cities/miami-theme/codigrate-miami.json',
            screenshot: {
              src: './assets/themes/cities/miami-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'miami/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/cities/miami-theme/codigrate-miami.tmTheme',
            screenshot: {
              src: './assets/themes/cities/miami-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'miami/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/cities/miami-theme/codigrate-miami.json',
            screenshot: {
              src: './assets/themes/cities/miami-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'miami/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/cities/miami-theme/Codigrate%20Miami.zip',
            screenshot: {
              src: './assets/themes/cities/miami-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'miami/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/cities/miami-theme/themes/miami.json',
            screenshot: {
              src: './assets/themes/cities/miami-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'miami/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/cities/miami-theme/Codigrate%20Miami.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/cities/miami-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            status: 'done',
            url: 'https://line.me/S/shop/theme/detail?id=972cf23c-0a76-456a-aaa9-1dbf3d7485e0',
            screenshot: {
              src: './assets/themes/cities/miami-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/miami-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#532033',
          '#FF5FA2',
          '#FE8078',
          '#00D1C1',
          '#F8D273'
        ],
        palette: [
          '#33121D',
          '#532033',
          '#FF5FA2',
          '#FE788C',
          '#FE8078',
          '#F2A4A0',
          '#92B5E8',
          '#00D1C1',
          '#82D59F',
          '#F8D273',
        ],
        designTokens: {
          "primaryColor": '#da587f',
          "secondaryColor": '#FE8078',
          "tertiaryColor": '#F8D273',
          "editorBackground": "#33121D",
          "hover": "#5d224d",
          "inputBackground": "#270D16",
          "editorForeground": "#D5C6CE",
          "error": "#EC5B54",
          "errorFocused": "#F63F36",
          "info": "#53A5DC",
          "infoFocused": "#369FE5",
          "primaryForeground": "#D5C6CE",
          "secondaryForeground": "#CCA2B6",
          "success": "#6CC952",
          "successFocused": "#57D235",
          "surface": "#5D263A",
          "warning": "#E6B250",
          "warningFocused": "#F0AE32",
          "windowBackground": "#532033",
        }
      },
      {
        key: 'istanbul',
        releaseDate: '2026-01-03',
        editorialLabel: 'Trending',
        name: 'Istanbul',
        description: `Inspired by the soft daylight and sea breezes of Istanbul, this theme blends calm turquoise tones with warm historical accents to create a serene yet expressive coding environment. Light, airy backgrounds keep the editor clean and comfortable, while teals, aquas, and muted golden hues add clarity and focus to essential syntax elements.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/istanbul-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/29590-istanbul-theme',
        logo:
          './assets/themes/cities/istanbul-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Mediterranean', 'Historic', 'Airy'],
        mainColors: ['Teal', 'Blue'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/29590-istanbul-theme',
            externalId: '29590',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'istanbul/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-istanbul-theme',
            externalId: 'codigrate.cod-istanbul-theme',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/istanbul-theme/cjlcpahdceldmbpjbfglhmdlmonjhndf',
            externalId: 'cjlcpahdceldmbpjbfglhmdlmonjhndf',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-istanbul/',
            externalId: 'codigrate-istanbul',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-istanbul-theme',
            externalId: 'codigrate/cod-istanbul-theme',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'istanbul/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/cities/istanbul-theme/istanbul-theme.txt',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'istanbul/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/cities/istanbul-theme/Codigrate%20Istanbul.terminal',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'istanbul/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/cities/istanbul-theme/Codigrate%20Istanbul.itermcolors',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'istanbul/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/cities/istanbul-theme/Codigrate%20Istanbul',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'istanbul/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/cities/istanbul-theme/codigrate-istanbul.json',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'istanbul/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/cities/istanbul-theme/codigrate-istanbul.tmTheme',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'istanbul/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/cities/istanbul-theme/codigrate-istanbul.json',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'istanbul/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/cities/istanbul-theme/Codigrate%20Istanbul.zip',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'istanbul/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/cities/istanbul-theme/themes/istanbul.json',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'istanbul/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/cities/istanbul-theme/Codigrate%20Istanbul.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-16 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            // (home-page.component.ts).
            url: 'https://line.me/S/shop/theme/detail?id=20195c89-d8c1-45f0-b7e2-2a25cd8a1038',
            screenshot: {
              src: './assets/themes/cities/istanbul-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/istanbul-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#A3DDE5',
          '#219cca',
          '#EFEAD0',
          '#B87958',
          '#B8514D',
        ],
        palette: [
          '#FAFDFD',
          '#DBF0F1',
          '#A3DDE5',
          '#1190A1',
          '#0887B5',
          '#EFEAD0',
          '#B87958',
          '#B8514D',
          '#C16979',
          '#9C6E7C',
        ],
        designTokens: {
          "primaryColor": '#26bdd3',
          "secondaryColor": '#f4957f',
          "tertiaryColor": '#DBF0F1',
          "editorBackground": "#FAFDFD",
          "hover": "#badfde",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#083A41",
          "error": "#C15C53",
          "errorFocused": "#CA4236",
          "info": "#397FB7",
          "infoFocused": "#2876B4",
          "primaryForeground": "#083A41",
          "secondaryForeground": "#0E6876",
          "success": "#30A25E",
          "successFocused": "#219D53",
          "surface": "#91D1DA",
          "warning": "#BDA051",
          "warningFocused": "#C39D37",
          "windowBackground": "#DBF0F1",
        }
      },
      {
        key: 'paris',
        releaseDate: '2026-01-05',
        name: 'Paris',
        editorialLabel: 'Designer\'s Choice',
        description: `Inspired by elegant boulevards and Paris's sunset glow, this dark theme trades bright champagne for dusty rose accents over calm plum-espresso tones and soft dark editor backgrounds. Mauve surfaces and wine-tinted hovers add depth and balance while focus stays clear, with a gentle blush accent guiding attention across the interface.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/paris-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/29603-paris-theme',
        logo:
          './assets/themes/cities/paris-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Romantic', 'Elegant', 'Sunset'],
        mainColors: ['Pink'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/29603-paris-theme',
            externalId: '29603',
            screenshot: {
              src: './assets/themes/cities/paris-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'paris/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/paris-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-paris-theme',
            externalId: 'codigrate.cod-paris-theme',
            screenshot: {
              src: './assets/themes/cities/paris-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/paris-theme/jcneihnpahfoamjdncacanalmdiokkbj',
            externalId: 'jcneihnpahfoamjdncacanalmdiokkbj',
            screenshot: {
              src: './assets/themes/cities/paris-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-paris/',
            externalId: 'codigrate-paris',
            screenshot: {
              src: './assets/themes/cities/paris-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-paris-theme',
            externalId: 'codigrate/cod-paris-theme',
            screenshot: {
              src: './assets/themes/cities/paris-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'paris/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/cities/paris-theme/paris-theme.txt',
            screenshot: {
              src: './assets/themes/cities/paris-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'paris/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/cities/paris-theme/Codigrate%20Paris.terminal',
            screenshot: {
              src: './assets/themes/cities/paris-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'paris/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/cities/paris-theme/Codigrate%20Paris.itermcolors',
            screenshot: {
              src: './assets/themes/cities/paris-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'paris/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/cities/paris-theme/Codigrate%20Paris',
            screenshot: {
              src: './assets/themes/cities/paris-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'paris/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/cities/paris-theme/codigrate-paris.json',
            screenshot: {
              src: './assets/themes/cities/paris-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'paris/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/cities/paris-theme/codigrate-paris.tmTheme',
            screenshot: {
              src: './assets/themes/cities/paris-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'paris/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/cities/paris-theme/codigrate-paris.json',
            screenshot: {
              src: './assets/themes/cities/paris-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'paris/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/cities/paris-theme/Codigrate%20Paris.zip',
            screenshot: {
              src: './assets/themes/cities/paris-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'paris/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/cities/paris-theme/themes/paris.json',
            screenshot: {
              src: './assets/themes/cities/paris-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'paris/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/cities/paris-theme/Codigrate%20Paris.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/cities/paris-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-16 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the
            // `status` key comes off: `done` is the default when the key is absent,
            // and only `done` cells render a working CTA rather than an inactive
            // "Coming soon" (home-page.component.ts).
            url: 'https://line.me/S/shop/theme/detail?id=73987fd9-608f-4f1c-aaf0-082a97efddd9',
            screenshot: {
              src: './assets/themes/cities/paris-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/paris-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#6A3C4D',
          '#D584A3',
          '#DF7583',
          '#FBBA77',
          '#87A1D3'
        ],
        palette: [
          '#281D22',
          '#4F303C',
          '#6A3C4D',
          '#D584A3',
          '#DF7583',
          '#F3B7A9',
          '#FBBA77',
          '#F1C970',
          '#5E7BB3',
          '#87A1D3',
        ],
        designTokens: {
          "primaryColor": '#a3506e',
          "secondaryColor": '#ef9cbb',
          "tertiaryColor": '#5E7BB3',
          "editorBackground": "#281D22",
          "hover": "#722832",
          "inputBackground": "#100d0e",
          "editorForeground": "#E6D7DD",
          "error": "#D15A56",
          "errorFocused": "#DA3E39",
          "info": "#5A8AC0",
          "infoFocused": "#3E7FC8",
          "primaryForeground": "#E6D7DD",
          "secondaryForeground": "#C79FAE",
          "success": "#5CA86C",
          "successFocused": "#46A95B",
          "surface": "#6A3C4D",
          "warning": "#D5A24C",
          "warningFocused": "#DE9D2E",
          "windowBackground": "#4F303C",
        }
      },
      {
        key: 'rio-de-janeiro',
        releaseDate: '2026-01-06',
        name: 'Rio de Janeiro',
        editorialLabel: 'Vibe Coder\'s Choice',
        description: `Inspired by Rio's lush hills, soft morning light, and ocean air, this theme blends airy minty backgrounds with confident rainforest greens and clean coastal blues. The editor stays bright and calm for long sessions, while crisp greens and balanced accents keep syntax readable and focused.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/rio-de-janeiro-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/29621-rio-de-janeiro-theme',
        logo:
          './assets/themes/cities/rio-de-janeiro-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Tropical', 'Coastal', 'Energetic'],
        mainColors: ['Green'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/29621-rio-de-janeiro-theme',
            externalId: '29621',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'rio-de-janeiro/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-rio-de-janeiro-theme',
            externalId: 'codigrate.cod-rio-de-janeiro-theme',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/rio-de-janeiro-theme/docafppdkimloimielffokjbnpfjaaie',
            externalId: 'docafppdkimloimielffokjbnpfjaaie',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-rio-de-janeiro/',
            externalId: 'codigrate-rio-de-janeiro',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-rio-de-janeiro-theme',
            externalId: 'codigrate/cod-rio-de-janeiro-theme',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'rio-de-janeiro/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/cities/rio-de-janeiro-theme/rio-de-janeiro-theme.txt',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'rio-de-janeiro/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/cities/rio-de-janeiro-theme/Codigrate%20Rio%20de%20Janeiro.terminal',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'rio-de-janeiro/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/cities/rio-de-janeiro-theme/Codigrate%20Rio%20de%20Janeiro.itermcolors',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'rio-de-janeiro/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/cities/rio-de-janeiro-theme/Codigrate%20Rio%20de%20Janeiro',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'rio-de-janeiro/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/cities/rio-de-janeiro-theme/codigrate-rio-de-janeiro.json',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'rio-de-janeiro/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/cities/rio-de-janeiro-theme/codigrate-rio-de-janeiro.tmTheme',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'rio-de-janeiro/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/cities/rio-de-janeiro-theme/codigrate-rio-de-janeiro.json',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'rio-de-janeiro/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/cities/rio-de-janeiro-theme/Codigrate%20Rio%20de%20Janeiro.zip',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'rio-de-janeiro/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/cities/rio-de-janeiro-theme/themes/rio-de-janeiro.json',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'rio-de-janeiro/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/cities/rio-de-janeiro-theme/Codigrate%20Rio%20de%20Janeiro.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-16 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the
            // `status` key comes off: `done` is the default when the key is absent,
            // and only `done` cells render a working CTA rather than an inactive
            // "Coming soon" (home-page.component.ts).
            url: 'https://line.me/S/shop/theme/detail?id=1fa63469-f208-42f7-93b1-e85077c9dfa6',
            screenshot: {
              src: './assets/themes/cities/rio-de-janeiro-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/rio-de-janeiro-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#D9EFD2',
          '#85B778',
          '#13A166',
          '#2287D5',
          '#ECDA61',
        ],
        palette: [
          '#F7FAF6',
          '#D9EFD2',
          '#85B778',
          '#13A166',
          '#028134',
          '#2287D5',
          '#0A80B3',
          '#1065B8',
          '#A3860A',
          '#ECDA61',
        ],
        designTokens: {
          "primaryColor": '#4ab631',
          "secondaryColor": '#E6C41A',
          "tertiaryColor": '#0A80B3',
          "editorBackground": "#F7FAF6",
          "hover": "#cadfc2",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#1A510A",
          "error": "#D15647",
          "errorFocused": "#DB3C29",
          "info": "#2287D5",
          "infoFocused": "#137DD0",
          "primaryForeground": "#1A510A",
          "secondaryForeground": "#2D7B18",
          "success": "#30A25E",
          "successFocused": "#219D53",
          "surface": "#85B778",
          "warning": "#E6C41A",
          "warningFocused": "#E1BE0A",
          "windowBackground": "#D9EFD2",
        }
      },
      {
        key: 'tokyo',
        releaseDate: '2026-01-07',
        name: 'Tokyo',
        editorialLabel: 'Popular',
        description: `Inspired by Tokyo's neon-lit side streets, midnight skylines, and the quiet glow of lantern-lined alleys, this theme blends deep indigo shadows with electric violet highlights to create a sleek, futuristic coding atmosphere. Moody blues keep the editor calm and focused, while luminous purples, soft lilacs, and crisp cyan accents add clarity and energy to key syntax elements.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/tokyo-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/29646-tokyo-theme',
        logo:
          './assets/themes/cities/tokyo-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Neon', 'Futuristic', 'Energetic'],
        mainColors: ['Purple'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/29646-tokyo-theme',
            externalId: '29646',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'tokyo/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-tokyo-theme',
            externalId: 'codigrate.cod-tokyo-theme',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/tokyo-theme/glgifonajfdadakpjjndpfnmmlaoeglb',
            externalId: 'glgifonajfdadakpjjndpfnmmlaoeglb',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-tokyo/',
            externalId: 'codigrate-tokyo',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-tokyo-theme',
            externalId: 'codigrate/cod-tokyo-theme',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'tokyo/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/cities/tokyo-theme/tokyo-theme.txt',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'tokyo/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/cities/tokyo-theme/Codigrate%20Tokyo.terminal',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'tokyo/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/cities/tokyo-theme/Codigrate%20Tokyo.itermcolors',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'tokyo/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/cities/tokyo-theme/Codigrate%20Tokyo',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'tokyo/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/cities/tokyo-theme/codigrate-tokyo.json',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'tokyo/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/cities/tokyo-theme/codigrate-tokyo.tmTheme',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'tokyo/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/cities/tokyo-theme/codigrate-tokyo.json',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'tokyo/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/cities/tokyo-theme/Codigrate%20Tokyo.zip',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'tokyo/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/cities/tokyo-theme/themes/tokyo.json',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'tokyo/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/cities/tokyo-theme/Codigrate%20Tokyo.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            status: 'done',
            url: 'https://line.me/S/shop/theme/detail?id=57cca0a9-6539-42c7-91c8-128430fa52f8',
            screenshot: {
              src: './assets/themes/cities/tokyo-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/tokyo-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#2A3051',
          '#7285DC',
          '#ECA1EB',
          '#5CC19D',
          '#5DC8D6'
        ],
        palette: [
          '#1A1F35',
          '#2A3051',
          '#7B89C8',
          '#7FA0DD',
          '#7285DC',
          '#ECA1EB',
          '#D3B690',
          '#DD9B7F',
          '#5CC19D',
          '#5DC8D6',
        ],
        designTokens: {
          "primaryColor": '#5f73dc',
          "secondaryColor": '#ECA1EB',
          "tertiaryColor": '#7B89C8',
          "editorBackground": "#1A1F35",
          "hover": "#5e3061",
          "inputBackground": "#0C0F1C",
          "editorForeground": "#AEB2C3",
          "error": "#CF5A56",
          "errorFocused": "#D83E39",
          "info": "#4A93D0",
          "infoFocused": "#2C8BD9",
          "primaryForeground": "#AEB2C3",
          "secondaryForeground": "#858EB7",
          "success": "#4FC093",
          "successFocused": "#34C68C",
          "surface": "#323B66",
          "warning": "#D0A24E",
          "warningFocused": "#D99D30",
          "windowBackground": "#2A3051",
        }
      },
      {
        key: 'tallinn',
        releaseDate: '2026-01-08',
        name: 'Tallinn',
        editorialLabel: 'Hidden Gem',
        description: `Inspired by Tallinn's crisp light and Baltic calm, this theme pairs airy porcelain backgrounds with cool Nordic blues for a clean, focused coding experience. Soft, bright surfaces enhance readability, while deep ink accents and subtle lavender-rose highlights add clarity and warmth without losing its minimal Northern character.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/tallinn-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/29671-tallinn-theme',
        logo:
          './assets/themes/cities/tallinn-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Nordic', 'Icy', 'Cool'],
        mainColors: ['Purple'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: false,
            url: 'https://plugins.jetbrains.com/plugin/29671-tallinn-theme',
            externalId: '29671',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'tallinn/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.vscode,
            paid: false,
            url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-tallinn-theme',
            externalId: 'codigrate.cod-tallinn-theme',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/tallinn-theme/efehigdahohhplollagfpnleobcaknpc',
            externalId: 'efehigdahohhplollagfpnleobcaknpc',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/chrome-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: false,
            url: 'https://addons.mozilla.org/en-US/firefox/addon/codigrate-tallinn/',
            externalId: 'codigrate-tallinn',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/firefox-screenshot-1.webp',
              description: 'Screenshot 3',
            },
          },
          {
            info: environmentCommon.website.openVsx,
            paid: false,
            url: 'https://open-vsx.org/extension/codigrate/cod-tallinn-theme',
            externalId: 'codigrate/cod-tallinn-theme',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/vscode-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: false, sku: 'tallinn/slack',
            url: 'https://raw.githubusercontent.com/codigrate/slack-themes/refs/heads/main/cities/tallinn-theme/tallinn-theme.txt',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/slack-screenshot-1.webp',
              description: 'Screenshot 4',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: false, sku: 'tallinn/macos-terminal',
            url: 'https://raw.githubusercontent.com/codigrate/macos-terminal-themes/refs/heads/main/cities/tallinn-theme/Codigrate%20Tallinn.terminal',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: false, sku: 'tallinn/iterm2',
            url: 'https://raw.githubusercontent.com/codigrate/iterm2-themes/refs/heads/main/cities/tallinn-theme/Codigrate%20Tallinn.itermcolors',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: false, sku: 'tallinn/ghostty',
            url: 'https://raw.githubusercontent.com/codigrate/ghostty-terminal-themes/refs/heads/main/cities/tallinn-theme/Codigrate%20Tallinn',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/ghostty-screenshot-1.webp',
              description: 'Screenshot 5',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: false, sku: 'tallinn/claude-code',
            url: 'https://raw.githubusercontent.com/codigrate/claude-code-themes/refs/heads/main/cities/tallinn-theme/codigrate-tallinn.json',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: false, sku: 'tallinn/codex',
            url: 'https://raw.githubusercontent.com/codigrate/codex-themes/refs/heads/main/cities/tallinn-theme/codigrate-tallinn.tmTheme',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: false, sku: 'tallinn/opencode',
            url: 'https://raw.githubusercontent.com/codigrate/opencode-themes/refs/heads/main/cities/tallinn-theme/codigrate-tallinn.json',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: false,
            sku: 'tallinn/rhino',
            url: 'https://raw.githubusercontent.com/codigrate/rhino-themes/refs/heads/main/cities/tallinn-theme/Codigrate%20Tallinn.zip',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: false, sku: 'tallinn/zed',
            url: 'https://raw.githubusercontent.com/codigrate/zed-themes/refs/heads/main/cities/tallinn-theme/themes/tallinn.json',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/zed-screenshot-1.webp',
              description: 'Zed',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: false, sku: 'tallinn/sublime-text',
            url: 'https://raw.githubusercontent.com/codigrate/sublime-text-themes/refs/heads/main/cities/tallinn-theme/Codigrate%20Tallinn.sublime-color-scheme',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-16 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the
            // `status` key comes off: `done` is the default when the key is absent,
            // and only `done` cells render a working CTA rather than an inactive
            // "Coming soon" (home-page.component.ts).
            url: 'https://line.me/S/shop/theme/detail?id=0c547757-369a-44e5-bed3-215b52e67899',
            screenshot: {
              src: './assets/themes/cities/tallinn-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/tallinn-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#D0DCEF',
          '#377CC1',
          '#425EB8',
          '#B6564B',
          '#B1544B'
        ],
        palette: [
          '#EDF2FA',
          '#D0DCEF',
          '#377CC1',
          '#425EB8',
          '#324979',
          '#81549C',
          '#B6564B',
          '#B1544B',
          '#548A64',
          '#1E7857',
        ],
        designTokens: {
          "primaryColor": '#678bda',
          "secondaryColor": '#9eb2f6',
          "tertiaryColor": '#D18E86',
          "editorBackground": "#EDF2FA",
          "hover": "#b7c3e3",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#0F244F",
          "error": "#C0564E",
          "errorFocused": "#C63E34",
          "info": "#397FB7",
          "infoFocused": "#2876B4",
          "primaryForeground": "#0F244F",
          "secondaryForeground": "#184092",
          "success": "#30A25E",
          "successFocused": "#219D53",
          "surface": "#A9B9DA",
          "warning": "#C39A4E",
          "warningFocused": "#CB9532",
          "windowBackground": "#D0DCEF",
        }
      },
      {
        key: 'prague',
        releaseDate: '2026-02-04',
        name: 'Prague',
        editorialLabel: 'Highly Recommended',
        description: `Inspired by Prague's midnight river reflections, gothic silhouettes, and warm lamplight on old stone, this theme pairs deep cobalt-navy shadows with cool steel blues for a calm, cinematic coding mood. Rich surfaces and crisp selections keep focus sharp, while a bright sky-blue accent and subtle golden highlights add contrast across tabs, hovers, and search matches.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/prague-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34266-prague-theme',
        logo:
          './assets/themes/cities/prague-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Gothic', 'Historic', 'Timeless'],
        mainColors: ['Blue'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34266-prague-theme',
            externalId: '34266',
            screenshot: {
              src: './assets/themes/cities/prague-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'prague/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'prague/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'prague/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'prague/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'prague/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'prague/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'prague/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'prague/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'prague/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'prague/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'prague/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'prague/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/prague-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=850df64b-c218-4c4a-9ca4-7dfb7d4f90f6',
            screenshot: {
              src: './assets/themes/cities/prague-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/prague-theme/background.webp',
            description: 'Background'
          }
        ],

        compactPalette: [
          '#113E6B',
          '#74ADF8',
          '#DF865B',
          '#EFBA5A',
          '#E3CA77'
        ],
        palette: [
          '#0D1D2E',
          '#123151',
          '#113E6B',
          '#74ADF8',
          '#89C5F8',
          '#92A3B3',
          '#DF865B',
          '#EFBA5A',
          '#E3CA77',
          '#DCCF9D',
        ],
        designTokens: {
          "primaryColor": '#2b74bf',
          "secondaryColor": '#EFBA5A',
          "tertiaryColor": '#DF865B',
          "editorBackground": "#0D1D2E",
          "hover": "#544410",
          "inputBackground": "#070F17",
          "editorForeground": "#BBC9D7",
          "error": "#D0605C",
          "errorFocused": "#D8453F",
          "info": "#4A93D8",
          "infoFocused": "#2C89E2",
          "primaryForeground": "#BBC9D7",
          "secondaryForeground": "#89A2BC",
          "success": "#4FBA80",
          "successFocused": "#37BD75",
          "surface": "#113E6B",
          "warning": "#D8A94A",
          "warningFocused": "#E2A52C",
          "windowBackground": "#123151",
        }
      },
      {
        key: 'sydney',
        releaseDate: '2026-02-05',
        name: 'Sydney',
        editorialLabel: 'Designer\'s Choice',
        description: `Inspired by Sydney's bright harbour mornings, salt-air breezes, and soft coastal light, this theme keeps the editor clean and airy with porcelain whites and misty blues. Gentle ocean surfaces and clear selections preserve focus, while a deep teal accent and warm sandstone highlights add contrast across tabs, borders, and search matches without losing the calm, sunlit feel.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/sydney-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34267-sydney-theme',
        logo:
          './assets/themes/cities/sydney-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Coastal', 'Bright', 'Modern'],
        mainColors: ['Blue'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34267-sydney-theme',
            externalId: '34267',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'sydney/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'sydney/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'sydney/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'sydney/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'sydney/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'sydney/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'sydney/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'sydney/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'sydney/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'sydney/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'sydney/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'sydney/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=ed20af30-1ad8-4949-8ea8-2b1c7f3d906c',
            screenshot: {
              src: './assets/themes/cities/sydney-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/sydney-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#CAE4EC',
          '#73B9D0',
          '#0F91C7',
          '#ECD268',
          '#AF8427'
        ],
        palette: [
          '#F6F9FA',
          '#CAE4EC',
          '#73B9D0',
          '#169DC5',
          '#0F91C7',
          '#2F9383',
          '#ECD268',
          '#AF8427',
          '#D17F51',
          '#BA7347',
        ],
        designTokens: {
          "primaryColor": '#19a5cf',
          "secondaryColor": '#e3b95a',
          "tertiaryColor": '#2F9383',
          "editorBackground": "#F6F9FA",
          "hover": "#ece7d1",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#062E3A",
          "error": "#C5564E",
          "errorFocused": "#CE3B31",
          "info": "#178BCA",
          "infoFocused": "#0A82C3",
          "primaryForeground": "#062E3A",
          "secondaryForeground": "#0E5E76",
          "success": "#30A25E",
          "successFocused": "#219D53",
          "surface": "#73B9D0",
          "warning": "#D6A840",
          "warningFocused": "#E0A621",
          "windowBackground": "#CAE4EC",
        }
      },
      {
        key: 'vienna',
        releaseDate: '2026-04-30',
        name: 'Vienna',
        editorialLabel: 'Highly Recommended',
        description: `Inspired by Vienna's refined elegance and cultural heritage, this dark theme creates a rich, immersive coding atmosphere in deep velvet reds and warm burgundy tones. Subtle gold and soft rose accents add a sense of quiet prestige. The result is a calm, intimate environment built for long, uninterrupted sessions.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/vienna-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34270-vienna-theme',
        logo:
          // ?v= busts the CDN's 31-day cache of the first icon (redrawn 2026-09-16).
          './assets/themes/cities/vienna-theme/icon.webp?v=20260916c',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Royal', 'Velvet', 'Refined'],
        mainColors: ['Red'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34270-vienna-theme',
            externalId: '34270',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'vienna/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'vienna/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'vienna/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'vienna/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'vienna/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'vienna/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'vienna/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'vienna/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'vienna/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'vienna/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'vienna/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'vienna/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=c28b5dca-8715-401a-98e8-1a0a054025f6',
            screenshot: {
              src: './assets/themes/cities/vienna-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/vienna-theme/background.webp',
            description: 'Background'
          }
        ],

        compactPalette: [
          '#6A2F34',
          '#D7999F',
          '#DCA826',
          '#EACF8C',
          '#98A67D'
        ],
        palette: [
          '#2C1D1D',
          '#4B2226',
          '#6A2F34',
          '#D7999F',
          '#DCA826',
          '#EACF8C',
          '#ECC39A',
          '#F4D8B3',
          '#98A67D',
          '#B6C39C',
        ],
        designTokens: {
          "primaryColor": '#a13f48',
          "secondaryColor": '#dac183',
          "tertiaryColor": '#6A2F34',
          "editorBackground": "#2C1D1D",
          "hover": "#615532",
          "inputBackground": "#0b0909",
          "editorForeground": "#E6D7D8",
          "error": "#D0605C",
          "errorFocused": "#D8453F",
          "info": "#5A93C8",
          "infoFocused": "#3D89D0",
          "primaryForeground": "#E6D7D8",
          "secondaryForeground": "#C0989C",
          "success": "#7DB075",
          "successFocused": "#67B65B",
          "surface": "#6A2F34",
          "warning": "#D6A94A",
          "warningFocused": "#D49A1E",
          "windowBackground": "#4B2226",
        }
      },
      {
        key: 'madrid',
        releaseDate: '2026-05-29',
        name: 'Madrid',
        editorialLabel: 'Vibe Coder\'s Choice',
        description: `Inspired by Madrid's sunlit limestone and the carmín flash of a flamenco dress, this light theme creates a warm, luminous coding atmosphere on saffron surfaces with terracotta accents. Azulejo teal and bougainvillea touches add Spanish summer warmth. The result is an elegant, vivid environment built for long, focused sessions.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/madrid-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34271-madrid-theme',
        logo:
          './assets/themes/cities/madrid-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Warm', 'Energetic', 'Mediterranean'],
        mainColors: ['Yellow'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34271-madrid-theme',
            externalId: '34271',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'madrid/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'madrid/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'madrid/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'madrid/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'madrid/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'madrid/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'madrid/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'madrid/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'madrid/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'madrid/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'madrid/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'madrid/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
          {
            info: environmentCommon.website.line,
            paid: true,
            // LIVE on the LINE store since 2026-08-18 — it cleared review, so the
            // placeholder store root is replaced by its own detail id and the entry
            // is `done`. There is no `status` key on a live platform: `done` is the
            // default, and only `done` cells render a working CTA
            url: 'https://line.me/S/shop/theme/detail?id=cc29c984-4612-4c97-b4bd-c79747a8c33d',
            screenshot: {
              src: './assets/themes/cities/madrid-theme/line-screenshot-1.webp',
              description: 'LINE Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/madrid-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#fdf5df',
          '#e8c25d',
          '#dca225',
          '#B84440',
          '#902926',
        ],
        palette: [
          '#FDF5DF',
          '#E8C25D',
          '#DCA225',
          '#A57F02',
          '#D37969',
          '#BD503F',
          '#B84440',
          '#902926',
          '#5A91A8',
          '#349590',
        ],
        designTokens: {
          "primaryColor": '#dda321',
          "secondaryColor": '#e86c59',
          "tertiaryColor": '#E8C25D',
          "editorBackground": "#FBFAF2",
          "hover": "#f8d0a3",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#372805",
          "error": "#CA6D68",
          "errorFocused": "#CE514B",
          "info": "#5692C3",
          "infoFocused": "#3887C8",
          "primaryForeground": "#372805",
          "secondaryForeground": "#745606",
          "success": "#40BB71",
          "successFocused": "#2FB263",
          "surface": "#E8C25D",
          "warning": "#D8B564",
          "warningFocused": "#DDAF46",
          "windowBackground": "#FDF5DF",
        }
      },
      {
        key: 'london',
        releaseDate: '2026-08-25',
        name: 'London',
        editorialLabel: 'New Release',
        description: `Inspired by London at dusk, this dark theme rests on a slate grey base washed with twilight indigo, then lights it with the pillar box red of a K6 telephone box and the muted steel blue of the Thames. The calm grey surface keeps long sessions easy on the eyes while the red accents mark what matters.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/london-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34274-london-theme',
        logo:
          // ?v= busts the CDN's 31-day cache of the first icon (redrawn 2026-09-16).
          './assets/themes/cities/london-theme/icon.webp?v=20260916',
        redirectToUrlAlter: true,
        appearance: 'Dark',
        tags: ['Royal', 'Atmospheric', 'Steel'],
        mainColors: ['Red', 'Gray'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34274-london-theme',
            externalId: '34274',
            screenshot: {
              src: './assets/themes/cities/london-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'london/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'london/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'london/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            // Sold on the site (cart -> Stripe Checkout -> download): the sku names
            // the store listing, the price comes from the store catalogue, no url.
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'london/sublime-text',
            // display fallback; the Stripe Price is what is charged. Keep them equal
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            // Sold on the site as well (see the Sublime Text listing above): the zip
            // ships the .terminal profile (base-themes generators/macos.js --premium).
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'london/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the .itermcolors preset
            // (base-themes generators/iterm2.js --premium).
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'london/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            // Sold on the site as well: the zip ships the Ghostty theme file
            // (base-themes generators/ghostty.js --premium).
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'london/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'london/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'london/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'london/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Windows .ini + the
            // signed/notarized Mac one-click app (base-themes generators/rhino.js --premium).
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'london/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            // Sold on the site as well (2026-09-21): the zip ships the Slack theme string
            // (base-themes generators/slack.js --premium).
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'london/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/london-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/london-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#3B3A45',
          '#EE4B5B',
          '#E88790',
          '#7FA3C6',
          '#DDDFEA'
        ],
        palette: [
          '#201F29',
          '#2D2B36',
          '#3B3A45',
          '#D42A44',
          '#EE4B5B',
          '#E88790',
          '#5D89B0',
          '#7FA3C6',
          '#A9ADC2',
          '#DDDFEA',
        ],
        designTokens: {
          "primaryColor": '#DE3B4B',
          "secondaryColor": '#7FA3C6',
          "tertiaryColor": '#A9ADC2',
          "editorBackground": "#201F29",
          "hover": "#282631",
          "inputBackground": "#17161D",
          "editorForeground": "#DDDFEA",
          "error": "#D93A3F",
          "errorFocused": "#EC4046",
          "info": "#5B9BD5",
          "infoFocused": "#6FAFE6",
          "primaryForeground": "#DDDFEA",
          "secondaryForeground": "#A9ADC2",
          "success": "#6FB98F",
          "successFocused": "#7FCB9E",
          "surface": "#3B3A45",
          "warning": "#D9A441",
          "warningFocused": "#E8B44A",
          "windowBackground": "#2D2B36",
        }
      },
      {
        key: 'dublin',
        releaseDate: '2026-09-29',
        name: 'Dublin',
        editorialLabel: 'New Release',
        description: `Inspired by Dublin on a bright morning, this light theme rests on a fresh park green base, then lights it with shamrock green and the orange of the autumn trees around the castle tower. The soft green surface keeps long sessions easy on the eyes while the green and orange accents mark what matters.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/cities/dublin-theme',
        urlAlter: 'https://plugins.jetbrains.com/plugin/34642-dublin-theme',
        logo: './assets/themes/cities/dublin-theme/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light',
        tags: ['Forest', 'Fresh', 'Historic'],
        mainColors: ['Green'],
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/34642-dublin-theme',
            externalId: '34642',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.xcode,
            paid: true,
            sku: 'dublin/xcode',
            listPrice: { amount: 299, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/xcode-screenshot-1.webp',
              description: 'Xcode Screenshot',
            },
          },
          {
            info: environmentCommon.website.zed,
            paid: true,
            sku: 'dublin/zed',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/zed-screenshot-1.webp',
              description: 'Zed Screenshot',
            },
          },
          {
            info: environmentCommon.website.firefox,
            paid: true,
            // sold on the site as the .xpi Mozilla signed (self-distributed, 2026-10-15)
            sku: 'dublin/firefox',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/firefox-screenshot-1.webp',
              description: 'Firefox Screenshot',
            },
          },
          {
            info: environmentCommon.website.sublimeText,
            paid: true,
            sku: 'dublin/sublime-text',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/sublime-screenshot-1.webp',
              description: 'Sublime Text Screenshot',
            },
          },
          {
            info: environmentCommon.website.macos,
            paid: true,
            sku: 'dublin/macos-terminal',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/macos-screenshot-1.webp',
              description: 'macOS Terminal Screenshot',
            },
          },
          {
            info: environmentCommon.website.iterm2,
            paid: true,
            sku: 'dublin/iterm2',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/iterm2-screenshot-1.webp',
              description: 'iTerm2 Screenshot',
            },
          },
          {
            info: environmentCommon.website.ghostty,
            paid: true,
            sku: 'dublin/ghostty',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/ghostty-screenshot-1.webp',
              description: 'Ghostty Screenshot',
            },
          },
          {
            info: environmentCommon.website.claudeCode,
            paid: true,
            sku: 'dublin/claude-code',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/claude-code-screenshot-1.webp',
              description: 'Claude Code Screenshot',
            },
          },
          {
            info: environmentCommon.website.codex,
            paid: true,
            sku: 'dublin/codex',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/codex-screenshot-1.webp',
              description: 'Codex Screenshot',
            },
          },
          {
            info: environmentCommon.website.opencode,
            paid: true,
            sku: 'dublin/opencode',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/opencode-screenshot-1.webp',
              description: 'OpenCode Screenshot',
            },
          },
          {
            info: environmentCommon.website.rhino,
            paid: true,
            sku: 'dublin/rhino',
            listPrice: { amount: 199, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/rhino-screenshot-1.webp',
              description: 'Rhino Screenshot',
            },
          },
          {
            info: environmentCommon.website.slack,
            paid: true,
            sku: 'dublin/slack',
            listPrice: { amount: 99, currency: 'usd' },
            url: '',
            screenshot: {
              src: './assets/themes/cities/dublin-theme/slack-screenshot-1.webp',
              description: 'Slack Screenshot',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/cities/dublin-theme/background.webp',
            description: 'Background'
          }
        ],
        compactPalette: [
          '#D3F3DB',
          '#6CC682',
          '#169B62',
          '#E26B03',
          '#FACA73',
        ],
        palette: [
          '#F1F9F3',
          '#D3F3DB',
          '#6CC682',
          '#169B62',
          '#1F9140',
          '#E26B03',
          '#CF6106',
          '#BD7135',
          '#FACA73',
          '#A38063',
        ],
        designTokens: {
          "primaryColor": "#169B62",
          "secondaryColor": "#E26B03",
          "tertiaryColor": "#3B564C",
          "editorBackground": "#F1F9F3",
          "hover": "#FBE3C3",
          "inputBackground": "#FFFFFF",
          "editorForeground": "#0F2E1A",
          "error": "#C5453E",
          "errorFocused": "#CF2F27",
          "info": "#2F86C8",
          "infoFocused": "#1D7BC2",
          "primaryForeground": "#0F2E1A",
          "secondaryForeground": "#3B564C",
          "success": "#4CBB17",
          "successFocused": "#3FAE0E",
          "surface": "#6CC682",
          "warning": "#D6A03A",
          "warningFocused": "#E09A1E",
          "windowBackground": "#D3F3DB",
        }
      }
    ],
  },
  {
    key: 'all-in-one',
    name: 'Collections',
    description: `All in one themes`,
    products: [
      {
        key: 'all-in-one',
        releaseDate: '2025-12-28',
        name: 'All In One',
        isBundle: true,
        description: `All in One Themes is the complete theme experience in a single package, bringing together all Codigrate themes into one unified collection for JetBrains IDEs. From calm, light environments inspired by natural landscapes to rich, cinematic dark themes shaped by iconic cities, this bundle lets you switch moods, lighting, and atmosphere without disrupting your workflow.`,
        url:
          environmentCommon.website.github.url +
          '/' +
          environmentCommon.company.social.github +
          '/' +
          'jetbrains-themes/tree/main/all-in-one-themes',
        urlAlter: 'https://plugins.jetbrains.com/plugin/30132-all-in-one-themes',
        tags: ['Collection', 'Bundle', 'Package'],
        mainColors: ['Multicolor'],
        logo:
          './assets/themes/all-in-one-themes/icon.webp',
        redirectToUrlAlter: true,
        appearance: 'Light&Dark',
        platforms: [
          {
            info: environmentCommon.website.jetbrainsMarketplace,
            paid: true,
            url: 'https://plugins.jetbrains.com/plugin/30132-all-in-one-themes',
            externalId: '30132',
            screenshot: {
              src: './assets/themes/all-in-one-themes/jetbrains-screenshot-1.webp',
              description: 'Screenshot 1',
            },
          },
          {
            info: environmentCommon.website.chrome,
            paid: false,
            url: 'https://chromewebstore.google.com/detail/all-in-one-themes/iekicoldppmopekekolhdoofncnhhbeh',
            externalId: 'iekicoldppmopekekolhdoofncnhhbeh',
            screenshot: {
              src: './assets/themes/all-in-one-themes/chrome-screenshot-1.webp',
              description: 'Screenshot 2',
            },
          },
        ],
        backgroundImages: [
          {
            src:
              './assets/themes/all-in-one-themes/background.webp',
            description: 'Background'
          }
        ],
        palette: [],
        // All-in-One is a bundle of every theme, so it has no single identity:
        // its tokens are the site's default (blue) accent, and it ships NO surface
        // tokens so the IDE skin stays OFF and the site keeps its default look
        // (light/dark toggle intact).
        designTokens: {
          "primaryColor": '#08ade8',
          "secondaryColor": '#06dddd',
          "tertiaryColor": '#000000',
        },
        discounts: [
          {group: 'students and teachers', amount: 'Free', platforms: ['JetBrains']},
          {group: 'open-source projects', amount: 'Free', platforms: ['JetBrains']},
          {group: 'universities and educational organizations', amount: '50% Off', platforms: ['JetBrains']},
          {group: 'startups', amount: '50% Off', platforms: ['JetBrains']},
          {group: 'non-profit organizations', amount: '50% Off', platforms: ['JetBrains']},
        ]
      }
    ],
  }
];

/** the tools filed under Color Tools (the /tools page, the footer, the sitemap page); every other tool without '-to-' is a Generator */
export const COLOR_TOOL_KEYS = [ 'color-card', 'contrast-checker', 'color-blindness-simulator' ];

export const TOOLS: ProductLine[] = [
  {
    key: 'tools',
    name: 'Tools',
    description: 'Utility tools for your needs',
    products: [
      {
        key: 'color-card',
        name: 'Color Explorer',
        shortDescription: 'Explore tones, shades, schemes and color blindness',
        description: `Explore tones, shades, and tints, and discover a variety of matching schemes like complementary, analogous. Additionally, simulate color blindness.`,
        url: URLS.tools.color,
        logo: './assets/product-lines/tools/color-card.svg'
      },
      {
        key: 'palette-generator',
        name: 'Palette Generator',
        shortDescription: 'Build and customize color palettes for your designs',
        description: `Generate and customize color palettes for your designs. See how colors match and simulate color blindness.`,
        url: URLS.tools.palette,
        logo: './assets/product-lines/tools/color-palette.svg'
      },
      {
        key: 'gradient-generator',
        name: 'Gradient Generator',
        shortDescription: 'Build linear, radial and conic CSS gradients and copy the code',
        description: `Build linear, radial and conic CSS gradients from your own color stops, set the angle, and copy ready-to-use CSS.`,
        url: URLS.tools.gradient,
        logo: './assets/product-lines/tools/gradient-card.svg'
      },
      {
        key: 'wallpaper-generator',
        name: 'Wallpaper Generator',
        shortDescription: 'Turn any palette into a wallpaper for every screen',
        description: `Turn any color palette into a mesh, wave, aurora, low poly, stripe, orb, mountain, synthwave, circuit, forest, checkered, hexagon, terrazzo, isometric, Bauhaus or dune wallpaper, procedurally, and download it for 4K, 5K, ultrawide, MacBook, iPhone, Android and iPad.`,
        url: URLS.tools.wallpaper,
        logo: './assets/product-lines/tools/wallpaper-card.png'
      },
      {
        key: 'dynamic-wallpaper-generator',
        name: 'Dynamic Wallpaper',
        shortDescription: 'A macOS wallpaper that changes from sunrise to night',
        description: `Turn any color palette into a macOS dynamic wallpaper: one .heic, lit for every hour from sunrise to night, that changes with the time of day, the sun position or Light and Dark mode.`,
        url: URLS.tools.dynamicWallpaper,
        logo: './assets/product-lines/tools/dynamic-wallpaper-card.png'
      },
      {
        key: 'code-image-generator',
        name: 'Code Snippet Image Generator',
        shortDescription: 'Turn your code into a picture in any Codigrate theme',
        description: `Paste your code, pick a language and one of the 25 Codigrate themes, and download a sharp PNG of your snippet for social posts, slides, docs and READMEs.`,
        url: URLS.tools.codeImage,
        logo: './assets/product-lines/tools/code-image-card.svg'
      },
      {
        key: 'contrast-checker',
        name: 'Contrast Checker',
        shortDescription: 'Check the WCAG contrast of any text and background',
        description: `Check the WCAG contrast ratio of a text color on a background color for AA and AAA, large text and UI parts, see the APCA value, and get the closest colors that pass.`,
        url: URLS.tools.contrastChecker,
        logo: './assets/product-lines/tools/contrast-card.svg?v=20261015'
      },
      {
        key: 'color-blindness-simulator',
        name: 'Color Blindness Simulator',
        shortDescription: 'See any image as people with color blindness do',
        description: `Upload any image and see it through protanopia, deuteranopia, tritanopia, achromatopsia and four more color vision deficiencies, with a before and after split and a PNG download.`,
        url: URLS.tools.colorBlindnessSimulator,
        logo: './assets/product-lines/tools/color-blindness-card.svg'
      },
      {
        key: 'hex-to-rgb',
        name: 'HEX to RGB',
        shortDescription: 'Convert any hex color code to its rgb() value instantly',
        description: `Paste a hex color code and get its RGB value instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToRgb,
        logo: './assets/product-lines/tools/hex-to-rgb.svg'
      },
      {
        key: 'rgb-to-hex',
        name: 'RGB to HEX',
        shortDescription: 'Convert RGB color values to their hex codes instantly',
        description: `Enter red, green and blue values and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.rgbToHex,
        logo: './assets/product-lines/tools/rgb-to-hex.svg'
      },
      {
        key: 'hex-to-hsl',
        name: 'HEX to HSL',
        shortDescription: 'Convert hex color codes to hue, saturation and lightness',
        description: `Paste a hex color code and get its HSL value instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToHsl,
        logo: './assets/product-lines/tools/hex-to-hsl.svg'
      },
      {
        key: 'hsl-to-hex',
        name: 'HSL to HEX',
        shortDescription: 'Convert hue, saturation and lightness to hex codes instantly',
        description: `Enter hue, saturation and lightness values and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hslToHex,
        logo: './assets/product-lines/tools/hsl-to-hex.svg'
      },
      {
        key: 'hex-to-hsv',
        name: 'HEX to HSV',
        shortDescription: 'Convert hex color codes to hue, saturation and value',
        description: `Paste a hex color code and get its HSV (HSB) value instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToHsv,
        logo: './assets/product-lines/tools/hex-to-hsv.svg'
      },
      {
        key: 'hsv-to-hex',
        name: 'HSV to HEX',
        shortDescription: 'Convert hue, saturation and value to hex codes instantly',
        description: `Enter hue, saturation and value and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hsvToHex,
        logo: './assets/product-lines/tools/hsv-to-hex.svg'
      },
      {
        key: 'hex-to-hsb',
        name: 'HEX to HSB',
        shortDescription: 'Convert hex color codes to hue, saturation and brightness',
        description: `Paste a hex color code and get its HSB (HSV) value instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToHsb,
        logo: './assets/product-lines/tools/hex-to-hsb.svg'
      },
      {
        key: 'hsb-to-hex',
        name: 'HSB to HEX',
        shortDescription: 'Convert hue, saturation and brightness to hex codes instantly',
        description: `Enter hue, saturation and brightness and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hsbToHex,
        logo: './assets/product-lines/tools/hsb-to-hex.svg'
      },
      {
        key: 'hex-to-hwb',
        name: 'HEX to HWB',
        shortDescription: 'Convert hex color codes to hue, whiteness and blackness',
        description: `Paste a hex color code and get its hwb() value instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToHwb,
        logo: './assets/product-lines/tools/hex-to-hwb.svg'
      },
      {
        key: 'hwb-to-hex',
        name: 'HWB to HEX',
        shortDescription: 'Convert hue, whiteness and blackness to hex codes instantly',
        description: `Enter hue, whiteness and blackness and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hwbToHex,
        logo: './assets/product-lines/tools/hwb-to-hex.svg'
      },
      {
        key: 'hex-to-cmyk',
        name: 'HEX to CMYK',
        shortDescription: 'Convert hex color codes to cyan, magenta, yellow and key',
        description: `Paste a hex color code and get its CMYK ink values instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToCmyk,
        logo: './assets/product-lines/tools/hex-to-cmyk.svg'
      },
      {
        key: 'cmyk-to-hex',
        name: 'CMYK to HEX',
        shortDescription: 'Convert cyan, magenta, yellow and key to hex codes instantly',
        description: `Enter cyan, magenta, yellow and key values and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.cmykToHex,
        logo: './assets/product-lines/tools/cmyk-to-hex.svg'
      },
      {
        key: 'hex-to-oklab',
        name: 'HEX to OKLAB',
        shortDescription: 'Convert hex color codes to OKLAB lightness and a/b axes',
        description: `Paste a hex color code and get its OKLAB lightness and a/b axes instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToOklab,
        logo: './assets/product-lines/tools/hex-to-oklab.svg'
      },
      {
        key: 'oklab-to-hex',
        name: 'OKLAB to HEX',
        shortDescription: 'Convert OKLAB lightness and a/b axes to hex codes instantly',
        description: `Enter OKLAB lightness and the a and b axes and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.oklabToHex,
        logo: './assets/product-lines/tools/oklab-to-hex.svg'
      },
      {
        key: 'hex-to-oklch',
        name: 'HEX to OKLCH',
        shortDescription: 'Convert hex color codes to OKLCH lightness, chroma and hue',
        description: `Paste a hex color code and get its OKLCH lightness, chroma and hue instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToOklch,
        logo: './assets/product-lines/tools/hex-to-oklch.svg'
      },
      {
        key: 'oklch-to-hex',
        name: 'OKLCH to HEX',
        shortDescription: 'Convert OKLCH lightness, chroma and hue to hex codes instantly',
        description: `Enter OKLCH lightness, chroma and hue and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.oklchToHex,
        logo: './assets/product-lines/tools/oklch-to-hex.svg'
      },
      {
        key: 'hex-to-cielab',
        name: 'HEX to CIELAB',
        shortDescription: 'Convert hex color codes to CIELAB lightness and a/b axes',
        description: `Paste a hex color code and get its CIELAB lightness and a/b axes instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToCielab,
        logo: './assets/product-lines/tools/hex-to-cielab.svg'
      },
      {
        key: 'cielab-to-hex',
        name: 'CIELAB to HEX',
        shortDescription: 'Convert CIELAB lightness and a/b axes to hex codes instantly',
        description: `Enter CIELAB lightness and the a and b axes and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.cielabToHex,
        logo: './assets/product-lines/tools/cielab-to-hex.svg'
      },
      {
        key: 'hex-to-cielch',
        name: 'HEX to CIELCH',
        shortDescription: 'Convert hex color codes to CIELCH lightness, chroma and hue',
        description: `Paste a hex color code and get its CIELCH lightness, chroma and hue instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToCielch,
        logo: './assets/product-lines/tools/hex-to-cielch.svg'
      },
      {
        key: 'cielch-to-hex',
        name: 'CIELCH to HEX',
        shortDescription: 'Convert CIELCH lightness, chroma and hue to hex codes instantly',
        description: `Enter CIELCH lightness, chroma and hue and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.cielchToHex,
        logo: './assets/product-lines/tools/cielch-to-hex.svg'
      },
      {
        key: 'hex-to-cieluv',
        name: 'HEX to CIELUV',
        shortDescription: 'Convert hex color codes to CIELUV lightness and u/v axes',
        description: `Paste a hex color code and get its CIELUV lightness and u/v axes instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToCieluv,
        logo: './assets/product-lines/tools/hex-to-cieluv.svg?v=20261011'
      },
      {
        key: 'cieluv-to-hex',
        name: 'CIELUV to HEX',
        shortDescription: 'Convert CIELUV lightness and u/v axes to hex codes instantly',
        description: `Enter CIELUV lightness and u/v axes and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.cieluvToHex,
        logo: './assets/product-lines/tools/cieluv-to-hex.svg?v=20261011'
      },
      {
        key: 'hex-to-display-p3',
        name: 'HEX to Display P3',
        shortDescription: 'Convert hex color codes to Display P3 red, green and blue',
        description: `Paste a hex color code and get its Display P3 red, green and blue channels instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToDisplayP3,
        logo: './assets/product-lines/tools/hex-to-display-p3.svg?v=20260917'
      },
      {
        key: 'display-p3-to-hex',
        name: 'Display P3 to HEX',
        shortDescription: 'Convert Display P3 red, green and blue to hex codes instantly',
        description: `Enter Display P3 red, green and blue and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.displayP3ToHex,
        logo: './assets/product-lines/tools/display-p3-to-hex.svg?v=20260917'
      },
      {
        key: 'hex-to-xyz',
        name: 'HEX to XYZ',
        shortDescription: 'Convert hex color codes to CIE XYZ tristimulus values',
        description: `Paste a hex color code and get its CIE XYZ tristimulus values instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToXyz,
        logo: './assets/product-lines/tools/hex-to-xyz.svg?v=20260917'
      },
      {
        key: 'xyz-to-hex',
        name: 'XYZ to HEX',
        shortDescription: 'Convert CIE XYZ tristimulus values to hex codes instantly',
        description: `Enter CIE XYZ tristimulus values and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.xyzToHex,
        logo: './assets/product-lines/tools/xyz-to-hex.svg?v=20260917'
      },
      {
        key: 'hex-to-yxy',
        name: 'HEX to Yxy',
        shortDescription: 'Convert hex color codes to CIE Yxy luminance and chromaticity',
        description: `Paste a hex color code and get its CIE Yxy luminance and chromaticity coordinates instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToYxy,
        logo: './assets/product-lines/tools/hex-to-yxy.svg?v=20260917'
      },
      {
        key: 'yxy-to-hex',
        name: 'Yxy to HEX',
        shortDescription: 'Convert CIE Yxy luminance and chromaticity to hex codes instantly',
        description: `Enter CIE Yxy luminance and chromaticity and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.yxyToHex,
        logo: './assets/product-lines/tools/yxy-to-hex.svg?v=20260917'
      },
      {
        key: 'hex-to-hunter-lab',
        name: 'HEX to Hunter Lab',
        shortDescription: 'Convert hex color codes to Hunter Lab lightness and a/b axes',
        description: `Paste a hex color code and get its Hunter Lab lightness and a/b axes instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hexToHunterLab,
        logo: './assets/product-lines/tools/hex-to-hunter-lab.svg?v=20260917'
      },
      {
        key: 'hunter-lab-to-hex',
        name: 'Hunter Lab to HEX',
        shortDescription: 'Convert Hunter Lab lightness and a/b axes to hex codes instantly',
        description: `Enter Hunter Lab lightness and a/b axes and get the hex color code instantly, with a live preview and the math worked out step by step.`,
        url: URLS.tools.hunterLabToHex,
        logo: './assets/product-lines/tools/hunter-lab-to-hex.svg?v=20260917'
      },
    ]
  },
];


export const SERVICES: ProductLine[] = [
  {
    key: 'services',
    name: 'Services',
    description: 'Services that we provide',
    products: [
      {
        key: 'software-integration',
        name: 'Software Integration',
        shortDescription: 'Third-party API integrations and workflow automation, tested and shipped so your tools work together.',
        description: 'Codigrate is providing various types of integration solutions from payments service provider integrations to social media integrations. There are many challenges integrating third-party solutions. Our team can handle all of the challenging integration obstacles by designing, testing, debugging, and execution of the integration.',
        url: '',
        logo: './assets/product-lines/services/software-integration.webp',
      },
      {
        key: 'software-consultancy',
        name: 'Software Consultancy',
        shortDescription: 'Custom software, end to end. Our engineers architect, build, test and ship software shaped around your business.',
        description: 'Our team perform custom software development services for businesses. Codigrate\'s experienced software developers can design, test and deploy your customized software and UI/UX designers can create unique user-interfaces for your company.',
        url: '',
        logo: './assets/product-lines/services/software-consultancy.webp',
      },
      {
        key: 'talent-discovery-solutions',
        name: 'Talent Discovery Solutions',
        shortDescription: 'AI-assisted candidate sourcing across millions of profiles. We rank the best-fit talent by skill, location and experience, then feed the shortlist into your hiring pipeline.',
        description: 'With our talent discovery solutions you can access to millions of smartly selected talents to increase the speed of your hiring process. With our customized integration solutions your company can easily find possible candidates in a matter of seconds.',
        url: '',
        logo: './assets/product-lines/services/talent-discovery-solutions.webp',
        demonstrationImg: {
          src: './assets/product-lines/services/talent-discovery-solutions.webp',
          description: 'talents'
        },
        techStack: [
          TECH_STACK.linkedIn,
          TECH_STACK.gitHub,
          TECH_STACK.stackOverflow,
        ],
        features: [
          {
            name: 'Analyze',
            subTitle: 'candidates by skill, location and experience',
            description: 'With the help of the AI',
            details: [
              {
                description: 'You can search and find most suitable candidates from different platforms'
              },
            ],
            image: {
              src: './assets/product-lines/services/features/talent-discovery-solutions-1.png',
              description: 'analyze'
            },
          },
          {
            name: 'Reports',
            subTitle: 'that helps you to visualize the results',
            description: 'Understand analyze results',
            details: [
              {
                description: 'with the built-in charts or export the report in various formats'
              },
            ],
            image: {
              src: './assets/product-lines/services/features/talent-discovery-solutions-2.png',
              description: 'analyze'
            },
          },
          {
            name: 'Review',
            subTitle: 'and find best possible candidates',
            description: 'Find best possible candidates',
            details: [
              {
                description: 'and integrate with your system to automate your HR process, system will learn from your feedback'
              },
            ],
            image: {
              src: './assets/product-lines/services/features/talent-discovery-solutions-3.png',
              description: 'analyze'
            },
          },
        ]
      },
      {
        key: 'mobile-app-development',
        name: 'Mobile App Development',
        shortDescription: 'Custom mobile apps built and integrated with your backend.',
        description: 'Codigrate can develop custom mobile applications for businesses. Our developers can develop your customized mobile application and integrate it with your servers, and UI/UX designers can create unique user-interfaces for your company.',
        url: '',
        logo: './assets/product-lines/services/mobile-app-development.webp',
      },
      {
        key: 'payment-gateway-integration',
        name: 'Payment Gateway Integration',
        shortDescription: 'Stripe, PayPal and other providers wired into your products, with secure, well-tested payment flows across checkout, subscriptions, billing and reconciliation.',
        description: 'Integrating your business with payment service providers can be challenging. We can integrate your products with payment service providers. Our developers\' experience is shaped around Stripe, PayPal.',
        url: '',
        logo: './assets/product-lines/services/payment-gateway-integration.webp',
      },
    ],
  },
];
